import React, { useMemo, useState } from 'react';
import {
  Layout,
  Input,
  Card,
  Button,
  Table,
  Tag,
  Space,
  Form,
  Modal,
  Select,
  Typography,
  Row,
  Col,
  message,
  ConfigProvider,
  Empty,
  Radio,
} from 'antd';
import {
  FileOutlined,
} from '@ant-design/icons';
import { buildTheme } from './config/theme';
import { buildSidebarStyles } from './config/sidebarStyles';
import { brandSemantic, applyPalette } from './config/brand';
import {
  canDeleteRecords,
  canManageProducts,
  canManageSupplierVisibility,
  canViewPrivateSuppliers,
  canViewSuppliersInCounterpartyList,
  filterVisibleSupplierProducts,
  filterVisibleSuppliers,
} from './config/permissions';
import { copyProductDocuments } from './utils/productCopy';
import { nextCustomProductNumber, nextStandardFallbackNumber } from './utils/productNumbering';
import { CONFIRMED_OFFER_STATUSES } from './constants/statuses';
import {
  buildInquiryId,
  buildQuoteId,
  buildOfferItemsFromInquiry,
  isSalesUser,
  buildInquiryItem,
  updateInquiryItem,
  deleteInquiryItem,
  buildQuotesFromInquiry,
} from './utils/inquiryHelpers';
import {
  getOfferLineItems as buildOfferLineItems,
  getOfferSupplierSections as buildOfferSupplierSections,
} from './utils/offerHelpers';
import {
  migrateLegacySupplierType,
} from './utils/supplierHelpers';
import {
  isReadyForOffer,
  saveSupplierQuote,
  buildOfferItemsFromQuotes,
  connectQuotesToOffer,
  computeOfferItemTotal,
  getCompleteSupplierQuoteOptions,
  getSelectedSupplierQuote,
  getAcceptedSupplierQuoteLines,
  migrateLegacySupplierQuotes,
} from './utils/quoteHelpers';
import {
  allItemsAccepted,
} from './utils/orderHelpers';
import {
  createCommentNotifications,
  createDocumentConvertedNotifications,
  createInquiryAssignedNotifications,
  createQuotesCreatedNotifications,
  createStatusChangeNotifications,
} from './utils/notificationHelpers';
import {
  initialCertificates,
  initialComments,
  initialContactPersons,
  initialContacts,
  initialDocuments,
  initialFiles,
  initialFollowUps,
  initialInquiries,
  initialLeads,
  initialLogisticsItems,
  initialNotifications,
  initialOffers,
  initialOrderSuppliers,
  initialProductCategories,
  initialProductDocuments,
  initialProductionNotes,
  initialProducts,
  initialQuotes,
  initialServiceItems,
  initialSupplierProducts,
  initialSuppliers,
  initialUsers,
} from './data/mockData';
import LoginPage from './components/LoginPage';
import AppSidebar from './components/AppSidebar';
import AppHeader from './components/AppHeader';
import StartPage from './pages/StartPage';
import OrdersPage from './pages/OrdersPage';
import InquiriesPage from './pages/InquiriesPage';
import QuotesPage from './pages/QuotesPage';
import FollowUpsPage from './pages/FollowUpsPage';
import EntityDetailsPage from './pages/EntityDetailsPage';
import PersonalProfilePage from './pages/PersonalProfilePage';
import ContactsListPage from './pages/ContactsListPage';
import LeadsListPage from './pages/LeadsListPage';
import ProductsManagementPage from './pages/ProductsManagementPage';
import ProductDetailsPage from './pages/ProductDetailsPage';
import UsersManagementPage from './pages/UsersManagementPage';
import LeadProfilePage from './pages/LeadProfilePage';
import InquiryDetailPage from './pages/InquiryDetailPage';
import CategoryDetailPage from './pages/CategoryDetailPage';
import InquiryCreatePage from './pages/InquiryCreatePage';
import QuoteDetailPage from './pages/QuoteDetailPage';
import SupplierProfilePage from './pages/SupplierProfilePage';
import CompliancePage from './pages/CompliancePage';
import ContactModal from './modals/ContactModal';
import ContactDetailsModal from './modals/ContactDetailsModal';
import ContactPersonModal from './modals/ContactPersonModal';
import LeadModal from './modals/LeadModal';
import OfferOrderModal from './modals/OfferOrderModal';
import ProductModal from './modals/ProductModal';
import ProductCreateChooserModal from './modals/ProductCreateChooserModal';
import CopyProductModal from './modals/CopyProductModal';
import ProductDocumentModal from './modals/ProductDocumentModal';
import SupplierProductModal from './modals/SupplierProductModal';
import SupplierModal from './modals/SupplierModal';
import CommentsModal from './modals/CommentsModal';
import CounterpartyTypeModal from './modals/CounterpartyTypeModal';
import FollowUpsModal from './modals/FollowUpsModal';
import { createTranslator, getAntdLocale, I18nProvider } from './i18n';

const { Content } = Layout;
const { Text } = Typography;
const { confirm } = Modal;

export default function CRMSystem() {
  const [leadForm] = Form.useForm();
  const [userForm] = Form.useForm();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentView, setCurrentView] = useState('followUps');
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierProfileReturnView, setSupplierProfileReturnView] = useState('suppliers');
  const [leadProfileReturnView, setLeadProfileReturnView] = useState('leads');
  const [leadProfileActiveTab, setLeadProfileActiveTab] = useState('0');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDetailsReturnView, setProductDetailsReturnView] = useState('products');
  const [orderDetailsReturn, setOrderDetailsReturn] = useState({ view: 'offers' });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalType, setModalType] = useState('');
  const [viewingContact, setViewingContact] = useState(null);
  const [editingContact, setEditingContact] = useState(null);
  const [editingContactPerson, setEditingContactPerson] = useState(null);
  const [editingLead, setEditingLead] = useState(null);
  const [editingOffer, setEditingOffer] = useState(null);
  const [repeatingOffer, setRepeatingOffer] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingSupplierProduct, setEditingSupplierProduct] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [viewingComments, setViewingComments] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [files, setFiles] = useState(initialFiles);
  const [inquiries, setInquiries] = useState(initialInquiries);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [selectedInquiryItem, setSelectedInquiryItem] = useState(null);
  // In-progress Selection Wizard state, keyed by Inquiry_Item id — the
  // wizard itself only holds this in local state, which would otherwise be
  // lost every time the user follows a Product/Supplier link out of
  // Category Details (a real navigation to another view, not a modal) and
  // comes back. Kept here, above CategoryDetailPage, so it survives that
  // round trip; cleared once the wizard is actually Saved.
  const [wizardDrafts, setWizardDrafts] = useState({});
  const [inquiryReturnView, setInquiryReturnView] = useState('inquiries');
  const [inquiryCreateReturnView, setInquiryCreateReturnView] = useState('inquiries');
  const [inquiryCreateClientId, setInquiryCreateClientId] = useState(null);
  const [quotes, setQuotes] = useState(() => initialQuotes.map(migrateLegacySupplierQuotes));
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [quoteReturnView, setQuoteReturnView] = useState('quotes');
  const [conversionQuoteId, setConversionQuoteId] = useState(null);
  const [conversionOfferId, setConversionOfferId] = useState(null);

  // Editable status overrides: { offerId: newStatus }
  const [offerStatusOverrides, setOfferStatusOverrides] = useState({});

  // Follow-up & supplier viewer state
  const [viewingFollowUps, setViewingFollowUps] = useState(null);
  const [viewingFollowUpComments, setViewingFollowUpComments] = useState(null);
  const [viewingSuppliers, setViewingSuppliers] = useState(null);
  const [supplierStatusOverrides, setSupplierStatusOverrides] = useState({});
  const [newFollowUp, setNewFollowUp] = useState({ date: '', action: '', responsible: '', priority: 'Medium', status: 'Pending' });
  const [newFollowUpComment, setNewFollowUpComment] = useState('');
  const [addingFollowUp, setAddingFollowUp] = useState(false);
  const [editingFollowUpId, setEditingFollowUpId] = useState(null);

  // UI state
  const [language, setLanguage] = useState('en');
  const t = createTranslator(language);
  const [colorPalette, setColorPaletteState] = useState(() => (
    localStorage.getItem('crm-color-palette') || 'default'
  ));
  const setColorPalette = (name) => {
    applyPalette(name);
    localStorage.setItem('crm-color-palette', name);
    setColorPaletteState(name);
  };
  applyPalette(colorPalette);
  const theme = useMemo(buildTheme, [colorPalette]);
  const sidebarStyles = useMemo(buildSidebarStyles, [colorPalette]);

  // Filter states
  const [contactFilter, setContactFilter] = useState('my'); // 'my' or 'all'
  const [contactTypeFilter, setContactTypeFilter] = useState('all');
  const [contactDateRange, setContactDateRange] = useState([null, null]);
  const [offerStatusFilter, setOfferStatusFilter] = useState('all');
  const [productStatusFilter, setProductStatusFilter] = useState('all');
  const [productNameFilter, setProductNameFilter] = useState('');
  const [productCategoryFilter, setProductCategoryFilter] = useState('all');
  const [productTypeFilter, setProductTypeFilter] = useState('all');
  const [pendingProductType, setPendingProductType] = useState('Standard');
  const [copySourceProduct, setCopySourceProduct] = useState(null);
  // Set while the Add/Copy Product flow was opened from a Category Details
  // Selection Wizard's Products step (rather than Products Management) —
  // locks the new/copied product to the item's category, requires a price
  // so it immediately qualifies as a selectable Supplier_Product row, forces
  // Copy Existing to produce a Custom product, and routes the result back
  // into the wizard's own selection instead of navigating to Product Details.
  const [wizardProductContext, setWizardProductContext] = useState(null);
  const [leadTypeFilter, setLeadTypeFilter] = useState('all');
  const [leadSearchText, setLeadSearchText] = useState('');
  const [supplierSearchText, setSupplierSearchText] = useState('');

  // Products state
  const [products, setProducts] = useState(initialProducts);
  const [productCategories, setProductCategories] = useState(initialProductCategories);
  const [serviceItems, setServiceItems] = useState(initialServiceItems);
  const [logisticsItems, setLogisticsItems] = useState(initialLogisticsItems);
  const [supplierProducts, setSupplierProducts] = useState(initialSupplierProducts);
  const [productDocuments, setProductDocuments] = useState(initialProductDocuments);
  const [editingProductDocument, setEditingProductDocument] = useState(null);
  const [isProductDocumentModalOpen, setIsProductDocumentModalOpen] = useState(false);
  const [productionNotes, setProductionNotes] = useState(initialProductionNotes);

  // Supplier certificates state
  const [certificates, setCertificates] = useState(initialCertificates);
  const [documents, setDocuments] = useState(initialDocuments);

  // Suppliers state
  const [suppliers, setSuppliers] = useState(() => initialSuppliers.map(migrateLegacySupplierType));
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [offerItems, setOfferItems] = useState([]);

  // Comments state
  const [comments, setComments] = useState(initialComments);

  // Notification centre state
  const [notifications, setNotifications] = useState(initialNotifications);
  const getCurrentMaxNotificationId = () => notifications.reduce((max, n) => Math.max(max, n.id), 0);
  const appendNotifications = (records) => {
    if (records.length > 0) setNotifications((prev) => [...prev, ...records]);
  };

  // User data for admin panel
  const [users, setUsers] = useState(initialUsers);

  const [leads, setLeads] = useState(initialLeads);

  const [contacts, setContacts] = useState(initialContacts);
  const [contactPersons, setContactPersons] = useState(initialContactPersons);
  const visibleSuppliers = filterVisibleSuppliers(suppliers, currentUser);
  const visibleProducts = products;
  const visibleSupplierProducts = filterVisibleSupplierProducts(supplierProducts, visibleSuppliers);
  const currentUserCanDelete = canDeleteRecords(currentUser);
  const currentUserIsSales = isSalesUser(currentUser);

  // Helper function to check if a lead should be converted to customer based on accepted offers
  const hasAcceptedOffer = (clientId) => {
    return offers.some(offer => 
      offer.clientId === clientId && 
      CONFIRMED_OFFER_STATUSES.includes(getOfferStatus(offer.id, offer.status))
    );
  };

  // Filter leads to exclude those with accepted offers (they should be customers)
  const getFilteredLeads = () => {
    return leads.filter(lead => {
      // Only filter out Lead/Prospect entries that have accepted offers
      if (lead.status === 'Lead/Prospect' && hasAcceptedOffer(lead.id)) {
        return false;
      }
      return true;
    });
  };

  const [offers, setOffers] = useState(initialOffers);

  // ── Follow-up registrations (per offer/order) ──────────────────────────────
  const [followUps, setFollowUps] = useState(initialFollowUps);

  // ── Supplier line-items per order (read-only demo data) ───────────────────
  const [orderSuppliers, setOrderSuppliers] = useState(initialOrderSuppliers);

  const getOfferSupplierSections = (offerId) => buildOfferSupplierSections({
    offerId,
    offers,
    orderSuppliers,
    suppliers: visibleSuppliers,
    products: visibleProducts,
    supplierProducts: visibleSupplierProducts,
  });

  const getOfferLineItems = (offerId) => buildOfferLineItems({
    offerId,
    offers,
    orderSuppliers,
    suppliers: visibleSuppliers,
    products: visibleProducts,
    supplierProducts: visibleSupplierProducts,
    services: serviceItems,
    logisticsItems,
  });

  const maskPrivateSupplierSections = (sections) => sections.map((section) => {
    if (canViewPrivateSuppliers(currentUser)) return section;
    const supplierRecord = suppliers.find((supplier) => supplier.name === section.name);
    if ((supplierRecord?.visibility || 'Public') !== 'Private') return section;
    return {
      ...section,
      displayName: t('Private supplier'),
    };
  });

  const getCompanyPrefix = (companyName) => {
    const letters = String(companyName || '')
      .toUpperCase()
      .replace(/[^A-Z]/g, '');
    return (letters || 'XXX').slice(0, 3).padEnd(3, 'X');
  };

  const getCompactDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  };

  const getIsoDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const buildOfferId = (companyName, creationDate) => {
    const baseId = `${getCompanyPrefix(companyName)}-${getCompactDate(creationDate)}`;
    const sameDayCount = offers.filter((offer) => offer.id === baseId || offer.id.startsWith(`${baseId}-`)).length;
    return sameDayCount === 0 ? baseId : `${baseId}-${String(sameDayCount + 1).padStart(2, '0')}`;
  };

  const buildSupplierSectionsFromOfferItems = (items) => {
    const grouped = new Map();

    items.forEach((item) => {
      const supplierName = item.supplierName || 'No supplier';
      if (!grouped.has(supplierName)) grouped.set(supplierName, []);

      const supplierItems = grouped.get(supplierName);
      supplierItems.push({
        row: supplierItems.length + 1,
        itemId: item.id,
        description: item.productName,
        article: item.article || '',
        qty: item.quantity,
        unitPrice: item.customerPrice ?? item.price ?? 0,
        customerPrice: item.customerPrice ?? item.price ?? 0,
        internalPrice: item.internalPrice ?? 0,
        costFromSupplier: item.internalPrice ?? 0,
        baseInternalPrice: item.baseInternalPrice ?? null,
        productionCost: item.productionCost ?? null,
        deliveryCost: item.deliveryCost ?? null,
        margin: item.margin ?? null,
        discount: item.discount || 0,
        internalLineTotal: item.internalLineTotal ?? ((item.internalPrice || 0) * (item.quantity || 0)),
        total: item.lineTotal,
        notes: item.notes || '',
        brandingDescription: item.brandingDescription || '',
        deliveryRequirements: item.deliveryRequirements || '',
        files: item.files || [],
      });
    });

    return Array.from(grouped.entries()).map(([name, items], index) => ({
      name,
      number: index + 1,
      status: 'Pending',
      poSent: getIsoDate(new Date()),
      items,
    }));
  };

  const buildFilesFromOfferItems = (items, offer, client) => items.flatMap((item) => (
    (item.files || []).map((file, fileIndex) => ({
      id: `${offer.id}-${item.id}-${fileIndex}-${Date.now()}`,
      clientId: client.id,
      offerId: offer.id,
      itemId: item.id,
      productName: item.productName,
      fileName: file.name,
      fileType: file.name.split('.').pop().toLowerCase(),
      description: file.description || `${item.productName} attachment`,
      uploadDate: new Date().toISOString().slice(0, 16).replace('T', ' '),
      uploadedBy: currentUser?.name || 'System',
      generatedFromOffer: false,
      source: 'Product attachment',
      fileObject: file.originFileObj,
      objectUrl: file.originFileObj ? URL.createObjectURL(file.originFileObj) : '',
    }))
  ));

  const handleSaveOfferOrder = ({
    type,
    clientId,
    descriptionToCustomer = '',
    deliveryDate = '',
    paymentTerms = '',
  }) => {
    const client = leads.find((lead) => lead.id === clientId) || selectedLead;
    if (!client) return null;

    const creationDate = new Date();
    const total = calculateTotal();
    const internalTotal = calculateInternalTotal();
    const offerId = editingOffer?.id || buildOfferId(client.name, creationDate);
    const newOffer = {
      ...(editingOffer || {}),
      key: editingOffer?.key || String(Date.now()),
      id: offerId,
      type,
      client: client.name,
      clientId: client.id,
      responsible: editingOffer?.responsible || currentUser?.name || '',
      date: editingOffer?.date || getIsoDate(creationDate),
      total: `${total.toLocaleString('da-DK')} DKK`,
      internalTotal: `${internalTotal.toLocaleString('da-DK')} DKK`,
      priority: editingOffer?.priority || 'Medium',
      status: editingOffer?.status || (type === 'Order' ? 'New Order' : 'New Sales Quote'),
      items: offerItems.length,
      descriptionToCustomer: descriptionToCustomer || editingOffer?.descriptionToCustomer || '',
      paymentTerms: paymentTerms || editingOffer?.paymentTerms || '',
      ...(type === 'Order' && conversionOfferId && !editingOffer ? { sourceOfferId: conversionOfferId } : {}),
      ...(type === 'Offer' && conversionQuoteId && !editingOffer ? { sourceQuoteId: conversionQuoteId } : {}),
      ...(type === 'Order'
        ? { deliveryDate: deliveryDate || editingOffer?.deliveryDate || getIsoDate(creationDate) }
        : { validUntil: editingOffer?.validUntil || getIsoDate(new Date(creationDate.getFullYear(), creationDate.getMonth() + 1, creationDate.getDate())) }),
      ...(type === 'Offer' && deliveryDate ? { deliveryDate } : {}),
    };

    setOffers((prev) => (
      editingOffer
        ? prev.map((offer) => (offer.id === editingOffer.id ? newOffer : offer))
        : [newOffer, ...prev]
    ));
    setOrderSuppliers((prev) => ({
      ...prev,
      [newOffer.id]: buildSupplierSectionsFromOfferItems(offerItems),
    }));
    setFiles((prev) => [
      ...prev.filter((file) => file.offerId !== newOffer.id || file.source !== 'Product attachment'),
      ...buildFilesFromOfferItems(offerItems, newOffer, client),
    ]);
    if (conversionQuoteId && !editingOffer) {
      setQuotes((prev) => prev.map((quote) => (
        quote.id === conversionQuoteId
          ? { ...quote, status: 'Sent to Sales Quote', convertedOfferId: newOffer.id }
          : quote
      )));
      setSelectedQuote((prev) => (
        prev?.id === conversionQuoteId
          ? { ...prev, status: 'Sent to Sales Quote', convertedOfferId: newOffer.id }
          : prev
      ));
      setConversionQuoteId(null);
      appendNotifications(createDocumentConvertedNotifications({
        fromType: 'Quote',
        fromId: conversionQuoteId,
        toType: 'Offer',
        toId: newOffer.id,
        actorId: currentUser?.id,
        users,
        currentMaxId: getCurrentMaxNotificationId(),
      }));
    }
    if (conversionOfferId && !editingOffer) {
      setOffers((prev) => prev.map((offer) => (
        offer.id === conversionOfferId
          ? { ...offer, convertedOrderId: newOffer.id }
          : offer
      )));
      setSelectedOrder((prev) => (
        prev?.id === conversionOfferId
          ? { ...prev, convertedOrderId: newOffer.id }
          : prev
      ));
      setConversionOfferId(null);
      appendNotifications(createDocumentConvertedNotifications({
        fromType: 'Offer',
        fromId: conversionOfferId,
        toType: 'Order',
        toId: newOffer.id,
        actorId: currentUser?.id,
        users,
        currentMaxId: getCurrentMaxNotificationId(),
      }));
    }
    return newOffer;
  };

  const handleLogin = (values) => {
    const user = users.find(u => u.login === values.username && u.password === values.password && u.active);
    if (user) {
      setCurrentUser(user);
      setLanguage(user.preferredLanguage || 'en');
      setIsLoggedIn(true);
      message.success(t('Login successful!'));
    } else {
      message.error(t('Invalid credentials'));
    }
  };

  const handleLogout = () => {
    setCurrentUser(null);
    setIsLoggedIn(false);
    setCurrentView('followUps');
    setNotifications(initialNotifications);
    message.info(t('Logged out successfully'));
  };

  const handleMarkNotificationRead = (id) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  };

  const handleMarkAllNotificationsRead = () => {
    setNotifications((prev) => prev.map((n) => (
      n.targetUserId === currentUser?.id ? { ...n, read: true } : n
    )));
  };

  const handleNotificationNavigate = (notification) => {
    handleMarkNotificationRead(notification.id);
    const { entityType, entityId } = notification;
    const notFound = () => message.info(t('The referenced document is no longer available'));

    if (entityType === 'Inquiry') {
      const inquiry = inquiries.find((item) => item.id === entityId);
      if (!inquiry) { notFound(); return; }
      openInquiryDetail(inquiry, 'inquiries');
      return;
    }
    if (entityType === 'Quote') {
      const quote = quotes.find((item) => item.id === entityId);
      if (!quote) { notFound(); return; }
      openQuoteDetail(quote, 'quotes');
      return;
    }
    if (entityType === 'Offer' || entityType === 'Order') {
      const offerOrOrder = offers.find((item) => item.id === entityId);
      if (!offerOrOrder) { notFound(); return; }
      openOrderDetails(offerOrOrder, entityType === 'Order' ? 'orders' : 'offers');
      return;
    }
    notFound();
  };

  const handlePersonalProfileSave = (values) => {
    if (!currentUser) return;

    const firstName = values.firstName.trim();
    const lastName = values.lastName.trim();
    const updatedUser = {
      ...currentUser,
      firstName,
      lastName,
      name: `${firstName} ${lastName}`.trim(),
      phone: values.phone.trim(),
      email: values.email.trim(),
      preferredLanguage: values.preferredLanguage,
    };

    setUsers((prevUsers) =>
      prevUsers.map((user) => (user.id === updatedUser.id ? updatedUser : user)),
    );
    setCurrentUser(updatedUser);
    setLanguage(updatedUser.preferredLanguage);
    message.success(t('Personal profile updated'));
  };

  const buildCounterpartyRecord = (values, existingLead = null) => {
    const numericIds = leads.map((lead) => Number(String(lead.id).slice(1))).filter((value) => !Number.isNaN(value));
    const nextNumericId = numericIds.length > 0 ? Math.max(...numericIds) + 1 : 1;
    const prefix = values.status === 'Customer' ? 'C' : 'L';

    return {
      key: existingLead?.key || String(leads.length + 1),
      id: existingLead?.id || `${prefix}${String(nextNumericId).padStart(3, '0')}`,
      name: values.name.trim(),
      cvr: values.cvr.trim(),
      email: values.email.trim(),
      phone: values.phone.trim(),
      status: values.status,
      created: existingLead?.created || new Date().toISOString().slice(0, 10),
      address: values.address?.trim() || '',
    };
  };

  const handleLeadSubmit = async () => {
    try {
      const values = await leadForm.validateFields();
      const leadRecord = buildCounterpartyRecord(values, editingLead);

      setLeads((prevLeads) => (
        editingLead
          ? prevLeads.map((lead) => (lead.id === editingLead.id ? leadRecord : lead))
          : [...prevLeads, leadRecord]
      ));

      if (selectedLead?.id === leadRecord.id) {
        setSelectedLead(leadRecord);
      }

      message.success(t(editingLead ? 'Client updated' : 'Lead created'));
      handleCancel();
      return leadRecord;
    } catch (error) {
      return null;
    }
  };

  const handleCreateCounterparty = (values) => {
    const leadRecord = buildCounterpartyRecord(values);
    setLeads((prevLeads) => [...prevLeads, leadRecord]);
    message.success(t('Counterparty created'));
    return leadRecord;
  };

  const handleContactPersonSubmit = (values) => {
    const entityId = selectedLead?.id ?? selectedSupplier?.id;
    if (!entityId) return;

    const contactPersonData = {
      id: editingContactPerson
        ? editingContactPerson.id
        : (contactPersons.length > 0 ? Math.max(...contactPersons.map((person) => person.id)) + 1 : 1),
      entityId,
      name: values.name?.trim() || '',
      position: values.position?.trim() || '',
      email: values.email?.trim() || '',
      phone: values.phone?.trim() || '',
    };

    setContactPersons((prevPersons) => (
      editingContactPerson
        ? prevPersons.map((person) => (person.id === editingContactPerson.id ? contactPersonData : person))
        : [...prevPersons, contactPersonData]
    ));

    message.success(t(editingContactPerson ? 'Contact person updated' : 'Contact person created'));
    handleCancel();
  };

  const handleUserStatusChange = (userId, nextActive) => {
    let updatedCurrentUser = null;

    setUsers((prevUsers) =>
      prevUsers.map((user) => {
        if (user.id !== userId) return user;
        const updatedUser = { ...user, active: nextActive };
        if (currentUser?.id === userId) updatedCurrentUser = updatedUser;
        return updatedUser;
      }),
    );

    if (updatedCurrentUser) {
      setCurrentUser(updatedCurrentUser);
    }

    message.success(t(`User marked as ${nextActive ? 'Active' : 'Inactive'}`));
  };

  const handleUserSubmit = async () => {
    try {
      const values = await userForm.validateFields();
      const firstName = values.firstName.trim();
      const lastName = values.lastName.trim();
      const userData = {
        ...(editingUser || {}),
        id: editingUser?.id || (users.length > 0 ? Math.max(...users.map((user) => user.id)) + 1 : 1),
        firstName,
        lastName,
        name: `${firstName} ${lastName}`.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
        login: values.login.trim(),
        password: values.password,
        role: values.role,
        preferredLanguage: values.preferredLanguage,
        active: values.active,
      };

      setUsers((prevUsers) => (
        editingUser
          ? prevUsers.map((user) => (user.id === editingUser.id ? userData : user))
          : [...prevUsers, userData]
      ));

      if (currentUser?.id === userData.id) {
        setCurrentUser(userData);
        setLanguage(userData.preferredLanguage || 'en');
      }

      message.success(t(editingUser ? 'User updated' : 'User created'));
      handleCancel();
    } catch (error) {
      // Validation errors are handled by the form UI.
    }
  };

  const openLeadProfile = (lead, returnView = 'leads') => {
    setSelectedLead(lead);
    setLeadProfileReturnView(returnView);
    setCurrentView('leadProfile');
  };

  const openSupplierProfile = (supplier, returnView = 'suppliers') => {
    setSelectedSupplier(supplier);
    setSupplierProfileReturnView(returnView);
    setCurrentView('supplierProfile');
  };

  const openOrderDetails = (order, returnView = 'offers', options = {}) => {
    setSelectedOrder(order);
    setOrderDetailsReturn({ view: returnView, ...options });
    setCurrentView('orderDetails');
  };

  const openInquiryDetail = (inquiry, returnView = 'offers') => {
    setSelectedInquiry(inquiry);
    setInquiryReturnView(returnView);
    setCurrentView('inquiryDetail');
  };

  const returnFromInquiryDetail = () => {
    if (inquiryReturnView === 'leadProfile') {
      setLeadProfileActiveTab('2');
    }
    setCurrentView(inquiryReturnView || 'inquiries');
  };

  const openCategoryDetail = (inquiry, item) => {
    setSelectedInquiry(inquiry);
    setSelectedInquiryItem(item);
    setCurrentView('categoryDetail');
  };

  const returnFromCategoryDetail = () => {
    setSelectedInquiryItem(null);
    setCurrentView('inquiryDetail');
  };

  const openInquiryCreate = (returnView = 'inquiries', clientId = null) => {
    setInquiryCreateReturnView(returnView);
    setInquiryCreateClientId(clientId);
    setCurrentView('inquiryCreate');
  };
  const returnFromInquiryCreate = () => {
    if (inquiryCreateReturnView === 'leadProfile') {
      setLeadProfileActiveTab('2');
    }
    setCurrentView(inquiryCreateReturnView || 'inquiries');
  };

  const openQuoteDetail = (quote, returnView = 'quotes') => {
    setSelectedQuote(quote);
    setQuoteReturnView(returnView);
    setCurrentView('quoteDetail');
  };

  const returnFromQuoteDetail = () => {
    if (quoteReturnView === 'leadProfile') {
      setLeadProfileActiveTab('3');
    }
    setCurrentView(quoteReturnView || 'quotes');
  };

  const openProductDetails = (product, returnView = 'products') => {
    setSelectedProduct(product);
    setProductDetailsReturnView(returnView);
    setCurrentView('productDetails');
  };

  const openCommentsModal = (clientId, clientName) => {
    setViewingComments({ clientId, clientName });
    setModalType('viewComments');
    setIsModalVisible(true);
  };

  const returnFromOrderDetails = () => {
    if (orderDetailsReturn.view === 'leadProfile') {
      setLeadProfileActiveTab(orderDetailsReturn.leadProfileTab || '0');
      setCurrentView('leadProfile');
      return;
    }
    if (orderDetailsReturn.view === 'supplierProfile') {
      setCurrentView('supplierProfile');
      return;
    }
    setCurrentView(orderDetailsReturn.view || 'offers');
  };

  const getEditableOfferItems = (offerId) => getOfferLineItems(offerId).map((item) => ({
    ...item,
    files: files
      .filter((file) => file.offerId === offerId && (file.itemId === item.itemId || file.itemId === item.id))
      .map((file) => ({
        uid: String(file.id),
        name: file.fileName,
        status: 'done',
        description: file.description,
        originFileObj: file.fileObject,
      })),
  }));

  const showModal = (type, data = null, options = {}) => {
    setModalType(type);
    if (type === 'viewContact') {
      setViewingContact(data);
    } else if (type === 'editLead') {
      setEditingLead(data);
      leadForm.setFieldsValue(data);
    } else if (type === 'createLead') {
      setEditingLead(null);
      leadForm.resetFields();
      leadForm.setFieldsValue({ status: 'Lead/Prospect' });
    } else if (type === 'editContact') {
      setEditingContact(data);
    } else if (type === 'createContactPerson') {
      setEditingContactPerson(null);
    } else if (type === 'editContactPerson') {
      setEditingContactPerson(data);
    } else if (type === 'editOffer') {
      setEditingOffer(data);
      setRepeatingOffer(null);
      setOfferItems(getEditableOfferItems(data.id));
    } else if (type === 'repeatOffer') {
      setEditingOffer(null);
      setRepeatingOffer(data);
      setOfferItems(getEditableOfferItems(data.id).map((item) => ({
        ...item,
        id: Date.now() + Math.floor(Math.random() * 100000),
      })));
    } else if (type === 'createOfferOrder' || type === 'createOffer' || type === 'createOrder') {
      setEditingOffer(null);
      setRepeatingOffer(null);
      if (!options.preserveOfferItems) {
        setOfferItems([]);
      }
      if (options.selectedLead) {
        setSelectedLead(options.selectedLead);
      }
    } else if (type === 'editUser') {
      setEditingUser(data);
      userForm.setFieldsValue({
        ...data,
        firstName: data.firstName || data.name?.split(' ')[0] || '',
        lastName: data.lastName || data.name?.split(' ').slice(1).join(' ') || '',
      });
    } else if (type === 'createUser') {
      setEditingUser(null);
      userForm.resetFields();
      userForm.setFieldsValue({
        role: 'sales user',
        preferredLanguage: 'en',
        active: true,
      });
    } else if (type === 'viewComments') {
      setViewingComments(data);
    } else if (type === 'viewFollowUps') {
      setViewingFollowUps(data);
      setAddingFollowUp(Boolean(options.startAdding));
      setEditingFollowUpId(null);
      setNewFollowUp({
        date: '',
        action: '',
        responsible: currentUser?.name || '',
        priority: 'Medium',
        status: 'Pending',
      });
    } else if (type === 'viewSuppliers') {
      setViewingSuppliers(data);
    }
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setModalType('');
    setViewingContact(null);
    setEditingContact(null);
    setEditingContactPerson(null);
    setEditingLead(null);
    setEditingOffer(null);
    setRepeatingOffer(null);
    setEditingProduct(null);
    setEditingSupplierProduct(null);
    setWizardProductContext(null);
    setEditingUser(null);
    setViewingComments(null);
    setViewingFollowUpComments(null);
    setNewComment('');
    setNewFollowUpComment('');
    setOfferItems([]);
    setViewingFollowUps(null);
    setViewingSuppliers(null);
    setSupplierStatusOverrides({});
    setConversionQuoteId(null);
    setConversionOfferId(null);
    setAddingFollowUp(false);
    setEditingFollowUpId(null);
    setNewFollowUp({ date: '', action: '', responsible: '', priority: 'Medium', status: 'Pending' });
    leadForm.resetFields();
    userForm.resetFields();
  };

  const handleSaveInquiry = (inquiryData) => {
    const creationDate = inquiryData.date || getIsoDate(new Date());
    const client = leads.find((lead) => lead.id === inquiryData.clientId) || selectedLead;
    const isEditing = Boolean(inquiryData.id);
    const existingInquiry = isEditing ? inquiries.find((inquiry) => inquiry.id === inquiryData.id) : null;
    const inquiryId = inquiryData.id || buildInquiryId(inquiryData.clientId, creationDate, inquiries);
    const inquiryRecord = {
      ...(existingInquiry || {}),
      ...inquiryData,
      key: inquiryId,
      id: inquiryId,
      date: inquiryData.date || existingInquiry?.date || creationDate,
      client: inquiryData.client || client?.name || '',
      status: isEditing ? (inquiryData.status || 'New Inquiry') : 'New Inquiry',
      convertedOfferId: inquiryData.convertedOfferId ?? null,
      attachments: inquiryData.attachments || existingInquiry?.attachments || [],
      items: inquiryData.items || existingInquiry?.items || [],
      createdBy: isEditing
        ? (inquiryData.createdBy || inquiries.find((inquiry) => inquiry.id === inquiryData.id)?.createdBy || currentUser?.name || 'System')
        : (inquiryData.createdBy || currentUser?.name || 'System'),
      createdAt: isEditing
        ? (inquiryData.createdAt || inquiries.find((inquiry) => inquiry.id === inquiryData.id)?.createdAt || new Date().toISOString().slice(0, 16).replace('T', ' '))
        : new Date().toISOString().slice(0, 16).replace('T', ' '),
      type: 'Inquiry',
      country: inquiryData.country ?? existingInquiry?.country ?? 'Denmark',
      nextStep: inquiryData.nextStep ?? existingInquiry?.nextStep ?? '',
      archived: isEditing ? (inquiryData.archived ?? existingInquiry?.archived ?? false) : false,
    };

    setInquiries((prev) => (
      isEditing
        ? prev.map((inquiry) => (inquiry.id === inquiryData.id ? inquiryRecord : inquiry))
        : [inquiryRecord, ...prev]
    ));
    setSelectedInquiry((prev) => (prev?.id === inquiryRecord.id ? inquiryRecord : prev));
    if (!isEditing) {
      appendNotifications(createInquiryAssignedNotifications({
        entityId: inquiryRecord.id,
        entityLabel: inquiryRecord.id,
        creatorId: currentUser?.id,
        users,
        currentMaxId: getCurrentMaxNotificationId(),
      }));
    }
    message.success(t(isEditing ? 'Inquiry updated' : 'Inquiry created'));
    return inquiryRecord;
  };

  const handleUpdateInquiry = (id, patch) => {
    setInquiries((prev) => prev.map((inquiry) => (
      inquiry.id === id ? { ...inquiry, ...patch } : inquiry
    )));
    setSelectedInquiry((prev) => (
      prev?.id === id ? { ...prev, ...patch } : prev
    ));
  };

  // ── Inquiry_Item CRUD (multi-product Inquiry — procurement workflow) ─────
  const handleAddInquiryItem = (inquiryId, itemData) => {
    const newItem = buildInquiryItem(itemData);
    const inquiry = inquiries.find((item) => item.id === inquiryId);
    handleUpdateInquiry(inquiryId, { items: [...(inquiry?.items || []), newItem] });
    message.success(t('Item added'));
  };

  const handleUpdateInquiryItem = (inquiryId, itemId, patch) => {
    const inquiry = inquiries.find((item) => item.id === inquiryId);
    if (!inquiry) return;
    handleUpdateInquiry(inquiryId, { items: updateInquiryItem(inquiry.items || [], itemId, patch) });
    message.success(t('Item updated'));
  };

  const handleDeleteInquiryItem = (inquiryId, itemId) => {
    const inquiry = inquiries.find((item) => item.id === inquiryId);
    if (!inquiry) return;
    handleUpdateInquiry(inquiryId, { items: deleteInquiryItem(inquiry.items || [], itemId) });
    message.success(t('Item removed'));
  };

  // Copies an entire Inquiry (header + items), with how much else comes along
  // depending on `mode`:
  //   'all'              -> products + selected suppliers + linked Quotes + files
  //   'products'         -> products only (no suppliers, no quotes, no files)
  //   'productsAndQuotes' -> products + selected suppliers + linked Quotes (no files)
  const handleCopyInquiry = (inquiryId, mode) => {
    const sourceInquiry = inquiries.find((item) => item.id === inquiryId);
    if (!sourceInquiry) return;

    const carrySuppliers = mode !== 'products';
    const carryQuotes = mode === 'productsAndQuotes' || mode === 'all';
    const carryFiles = mode === 'all';
    const creationDate = new Date();
    const newInquiryId = buildInquiryId(sourceInquiry.clientId, getIsoDate(creationDate), inquiries);

    const newQuotesToAdd = [];
    const newItems = (sourceInquiry.items || []).map((item) => {
      const newItem = buildInquiryItem({
        ...item,
        id: undefined,
        wizardResult: carrySuppliers ? item.wizardResult : null,
        files: carryFiles ? item.files : [],
      });

      const sourceQuote = carryQuotes
        ? quotes.find((quote) => quote.inquiryItemId === item.id && !quote.archived)
        : null;

      if (sourceQuote) {
        const newQuoteId = `${sourceQuote.id}-COPY-${Date.now()}-${newItem.id}`;
        newQuotesToAdd.push({
          ...sourceQuote,
          id: newQuoteId,
          key: newQuoteId,
          inquiryId: newInquiryId,
          inquiryItemId: newItem.id,
          convertedOfferId: null,
          archived: false,
        });
        newItem.sourcingStatus = 'Quote Created';
      } else {
        newItem.sourcingStatus = newItem.wizardResult ? 'Sourced' : 'Pending';
      }
      return newItem;
    });

    const newInquiry = {
      ...sourceInquiry,
      id: newInquiryId,
      key: newInquiryId,
      date: getIsoDate(creationDate),
      createdAt: new Date().toISOString().slice(0, 16).replace('T', ' '),
      createdBy: currentUser?.name || sourceInquiry.createdBy,
      status: 'New Inquiry',
      nextStep: 'Awaiting supplier pricing',
      archived: false,
      attachments: carryFiles ? (sourceInquiry.attachments || []) : [],
      items: newItems,
    };

    setInquiries((prev) => [newInquiry, ...prev]);
    if (newQuotesToAdd.length > 0) {
      setQuotes((prev) => [...newQuotesToAdd, ...prev]);
    }
    message.success(t('Inquiry copied: {{id}}', { id: newInquiryId }));
    openInquiryDetail(newInquiry, 'inquiries');
  };

  const handleCreateQuoteForInquiryItem = (inquiryId, itemIds) => {
    const inquiry = inquiries.find((item) => item.id === inquiryId);
    if (!inquiry) return;
    
    // Handle both single itemId (string) and multiple itemIds (array)
    const itemIdArray = Array.isArray(itemIds) ? itemIds : [itemIds];
    const newQuotes = buildQuotesFromInquiry(inquiry, quotes, itemIdArray);
    if (newQuotes.length === 0) return;
    
    setQuotes((prev) => [...newQuotes, ...prev]);
    handleUpdateInquiry(inquiryId, {
      items: (inquiry.items || []).map((item) => (
        itemIdArray.includes(item.id) ? { ...item, sourcingStatus: 'Quote Created' } : item
      )),
    });
    message.success(t(newQuotes.length === 1 ? 'Quote created' : `${newQuotes.length} quotes created`));
    appendNotifications(createQuotesCreatedNotifications({
      inquiryId,
      quoteIds: newQuotes.map((q) => q.id),
      actorId: currentUser?.id,
      users,
      currentMaxId: getCurrentMaxNotificationId(),
    }));
    openQuoteDetail(newQuotes[0], 'inquiryDetail');
  };

  const handleUpdateQuote = (id, patch) => {
    const before = quotes.find((quote) => quote.id === id);
    setQuotes((prev) => prev.map((quote) => (
      quote.id === id ? { ...quote, ...patch } : quote
    )));
    setSelectedQuote((prev) => (
      prev?.id === id ? { ...prev, ...patch } : prev
    ));
    if (patch.status && patch.status !== before?.status) {
      appendNotifications(createStatusChangeNotifications({
        entityType: 'Quote',
        entityId: id,
        entityLabel: id,
        newStatus: patch.status,
        actorId: currentUser?.id,
        users,
        currentMaxId: getCurrentMaxNotificationId(),
      }));
    }
    if (
      patch.status === 'Sent to Sales Quote'
      && before?.status !== 'Sent to Sales Quote'
      && !before?.convertedOfferId
    ) {
      confirm({
        title: t('Create Offer'),
        content: t('The Quote has been sent to Offer. Would you like to create an Offer based on this Quote?'),
        okText: t('Yes'),
        cancelText: t('No'),
        onOk: () => handleConvertQuoteToOffer({ ...before, ...patch }),
      });
    }
  };

  const handleCreateQuoteFromInquiry = (inquiry) => {
    const existing = quotes.find((quote) => quote.inquiryId === inquiry.id);
    if (existing) {
      openQuoteDetail(existing, 'inquiryDetail');
      return;
    }
    const creationDate = getIsoDate(new Date());
    const quoteId = buildQuoteId(inquiry.clientId, creationDate, quotes);
    const quoteRecord = {
      id: quoteId,
      key: quoteId,
      type: 'Quote',
      inquiryId: inquiry.id,
      clientId: inquiry.clientId,
      client: inquiry.client,
      requestedBy: inquiry.requestedBy,
      priority: inquiry.priority,
      neededBefore: inquiry.neededBefore,
      requestedDeliveryDate: inquiry.requestedDeliveryDate,
      productCategoryId: inquiry.productCategoryId,
      productCategoryName: inquiry.productCategoryName,
      requestedQuantity: inquiry.requestedQuantity,
      budgetTargetPrice: inquiry.budgetTargetPrice,
      printingDecorationType: inquiry.printingDecorationType,
      urgent: inquiry.urgent,
      reorder: inquiry.reorder,
      specialRequirements: inquiry.specialRequirements,
      notes: inquiry.notes,
      status: 'Ready for Procurement',
      createdBy: currentUser?.name || 'System',
      createdAt: new Date().toISOString().slice(0, 16).replace('T', ' '),
      convertedOfferId: null,
      items: [],
      country: inquiry.country || 'Denmark',
      nextStep: 'Awaiting supplier pricing',
      archived: false,
    };
    setQuotes((prev) => [quoteRecord, ...prev]);
    if (inquiry.status === 'New Inquiry') {
      handleUpdateInquiry(inquiry.id, { status: 'Sent to Procurement' });
    }
    message.success(t('Quote created'));
    openQuoteDetail(quoteRecord, 'inquiryDetail');
  };

  // Wraps handleUpdateInquiry for the status dropdown only, to also emit a
  // status-change notification. Quote creation from "Sent to Procurement" is
  // no longer prompted here — Quotes are created per Inquiry_Item via the
  // SelectionWizard (see handleCreateQuoteForInquiryItem), not per whole
  // Inquiry.
  const handleInquiryStatusChange = (id, patch) => {
    const before = inquiries.find((item) => item.id === id);
    handleUpdateInquiry(id, patch);
    if (patch.status && patch.status !== before?.status) {
      appendNotifications(createStatusChangeNotifications({
        entityType: 'Inquiry',
        entityId: id,
        entityLabel: id,
        newStatus: patch.status,
        actorId: currentUser?.id,
        users,
        currentMaxId: getCurrentMaxNotificationId(),
      }));
    }
  };

  const handleSaveQuoteQuotation = (id, itemQuotations) => {
    const applyQuotations = (quote) => ({
      ...quote,
      items: (quote.items || []).map((item) => ({
        ...item,
        quotation: itemQuotations[item.id] || item.quotation || null,
      })),
    });

    setQuotes((prev) => prev.map((quote) => (
      quote.id === id ? applyQuotations(quote) : quote
    )));
    setSelectedQuote((prev) => (prev?.id === id ? applyQuotations(prev) : prev));
  };

  const handleConvertQuoteToOffer = (quote) => {
    const client = leads.find((lead) => lead.id === quote.clientId);
    const convertedItems = buildOfferItemsFromInquiry(quote);
    setSelectedLead(client || null);
    setOfferItems(convertedItems);
    setConversionQuoteId(quote.id);
    showModal('createOffer', null, {
      preserveOfferItems: true,
      selectedLead: client || null,
    });
  };

  const handleConvertOfferToOrder = (offer) => {
    const client = leads.find((lead) => lead.id === offer.clientId);
    setSelectedLead(client || null);
    setOfferItems(getEditableOfferItems(offer.id));
    setConversionOfferId(offer.id);
    showModal('createOrder', null, {
      preserveOfferItems: true,
      selectedLead: client || null,
    });
  };

  // ── Quote — Supplier_Quote cost entry & Ready-for-Offer gate ─────────────
  const handleSaveSupplierQuote = (quoteId, supplierId, data) => {
    const applyPatch = (quote) => saveSupplierQuote(quote, supplierId, data);
    setQuotes((prev) => prev.map((quote) => (quote.id === quoteId ? applyPatch(quote) : quote)));
    setSelectedQuote((prev) => (prev?.id === quoteId ? applyPatch(prev) : prev));
    message.success(t('Supplier quote saved'));
  };

  const handleSetQuoteStatus = (quoteId, status) => {
    const quote = quotes.find((item) => item.id === quoteId);
    if (!quote) return;
    if (status === 'Ready for Sales Quote' && !isReadyForOffer(quote, suppliers)) {
      message.warning(t('At least one supplier quote needs unit price, production time, and shipping time before this can move to Ready for Sales Quote.'));
      return;
    }
    handleUpdateQuote(quoteId, { status });
  };

  // ── Quote → Offer connection ──────────────────────────────────────────────
  const handleConnectQuotesToOffer = (quoteIds, offerId, supplierOverrides = {}) => {
    const selectedQuotes = quotes.filter((quote) => quoteIds.includes(quote.id));
    const connected = connectQuotesToOffer(selectedQuotes, offerId);
    const connectedById = new Map(connected.map((quote) => [quote.id, quote]));
    setQuotes((prev) => prev.map((quote) => connectedById.get(quote.id) || quote));
    setSelectedQuote((prev) => (prev && connectedById.has(prev.id) ? connectedById.get(prev.id) : prev));
    const newItems = buildOfferItemsFromQuotes(selectedQuotes, supplierOverrides, suppliers);
    setOffers((prev) => prev.map((offer) => (
      offer.id === offerId
        ? {
          ...offer,
          items: [...(offer.items || []), ...newItems],
          sourceQuoteIds: Array.from(new Set([...(offer.sourceQuoteIds || []), ...quoteIds])),
        }
        : offer
    )));
    message.success(t('Connected to offer: {{id}}', { id: offerId }));
  };

  // ── Multiple Supplier_Quotes per Quote: ask which supplier to use ────────
  const [supplierPicker, setSupplierPicker] = useState(null);

  const findAmbiguousQuoteOptions = (quoteIds) => {
    const optionsByQuoteId = {};
    let hasAmbiguity = false;
    quoteIds.forEach((quoteId) => {
      const quote = quotes.find((item) => item.id === quoteId);
      const options = getCompleteSupplierQuoteOptions(quote || {}, suppliers);
      optionsByQuoteId[quoteId] = options;
      // Multiple Accepted suppliers isn't ambiguous — it's an explicit choice
      // to carry all of them into the Offer as separate line items.
      const acceptedCount = getAcceptedSupplierQuoteLines(quote || {}, suppliers).length;
      if (options.length > 1 && acceptedCount <= 1) hasAmbiguity = true;
    });
    return { optionsByQuoteId, hasAmbiguity };
  };

  const defaultSupplierSelections = (quoteIds) => {
    const selections = {};
    quoteIds.forEach((quoteId) => {
      const quote = quotes.find((item) => item.id === quoteId);
      const selected = quote && getSelectedSupplierQuote(quote, null, suppliers);
      if (selected) selections[quoteId] = selected.supplierId;
    });
    return selections;
  };

  const requestCreateOfferFromQuotes = (quoteIds) => {
    const selections = defaultSupplierSelections(quoteIds);
    Modal.confirm({
      title: t('Create Offer'),
      content: t('Create an offer from the selected supplier quote(s)?'),
      okText: t('Create'),
      cancelText: t('Cancel'),
      onOk: () => {
        const newOffer = handleCreateOfferFromQuotes(quoteIds, selections);
        if (newOffer) openOrderDetails(newOffer, 'quoteDetail');
      },
    });
  };

  const requestConnectQuotesToOffer = (quoteIds, offerId) => {
    const { optionsByQuoteId, hasAmbiguity } = findAmbiguousQuoteOptions(quoteIds);
    if (!hasAmbiguity) {
      handleConnectQuotesToOffer(quoteIds, offerId);
      return;
    }
    setSupplierPicker({
      mode: 'connect', quoteIds, offerId, optionsByQuoteId, selections: defaultSupplierSelections(quoteIds),
    });
  };

  const confirmSupplierPicker = () => {
    if (!supplierPicker) return;
    handleConnectQuotesToOffer(supplierPicker.quoteIds, supplierPicker.offerId, supplierPicker.selections);
    setSupplierPicker(null);
  };

  const handleCreateOfferFromQuotes = (quoteIds, supplierOverrides = {}) => {
    const selectedQuotes = quotes.filter((quote) => quoteIds.includes(quote.id));
    if (selectedQuotes.length === 0) return null;
    const client = leads.find((lead) => lead.id === selectedQuotes[0].clientId);
    const creationDate = new Date();
    const offerId = buildOfferId(client?.name || selectedQuotes[0].client, creationDate);
    const items = buildOfferItemsFromQuotes(selectedQuotes, supplierOverrides, suppliers);
    const total = items.reduce((sum, item) => sum + computeOfferItemTotal(item), 0);
    const newOffer = {
      key: offerId,
      id: offerId,
      type: 'Offer',
      client: client?.name || selectedQuotes[0].client,
      clientId: selectedQuotes[0].clientId,
      responsible: currentUser?.name || '',
      date: getIsoDate(creationDate),
      status: 'New Sales Quote',
      items,
      total: `${total.toLocaleString('da-DK')} DKK`,
      internalTotal: `${total.toLocaleString('da-DK')} DKK`,
      priority: 'Medium',
      validUntil: getIsoDate(new Date(creationDate.getFullYear(), creationDate.getMonth() + 1, creationDate.getDate())),
      sourceQuoteIds: quoteIds,
      archived: false,
    };
    setOffers((prev) => [newOffer, ...prev]);
    const connected = connectQuotesToOffer(selectedQuotes, offerId);
    const connectedById = new Map(connected.map((quote) => [quote.id, quote]));
    setQuotes((prev) => prev.map((quote) => connectedById.get(quote.id) || quote));
    setSelectedQuote((prev) => (prev && connectedById.has(prev.id) ? connectedById.get(prev.id) : prev));
    message.success(t('Offer created: {{id}}', { id: offerId }));
    return newOffer;
  };

  // ── Offer → Order (direct creation, no modal) ─────────────────────────────
  // Always produces a normal, per-supplier-grouped Order (same shape as the
  // manual "Create Offer/Order" modal), regardless of whether the source
  // Offer used the flat Offer_Item array (Quote→Offer auto-creation) or the
  // legacy item-count + orderSuppliers model.
  const handleCreateOrderFromOffer = (offer) => {
    const newOrderId = `${offer.id}-ORD`;
    const sourceItems = Array.isArray(offer.items)
      ? offer.items.map((item) => ({
        id: item.id,
        productName: item.productName,
        supplierName: item.supplierName,
        quantity: item.quantity,
        internalPrice: item.unitPrice,
        deliveryCost: item.shippingCost,
        margin: item.marginPercent,
        discount: item.discountPercent,
        internalLineTotal: Number(item.unitPrice || 0) * Number(item.quantity || 0) + Number(item.shippingCost || 0),
        lineTotal: computeOfferItemTotal(item),
        notes: '',
        brandingDescription: '',
        deliveryRequirements: '',
        files: [],
      }))
      : getEditableOfferItems(offer.id);
    const newOrder = {
      key: newOrderId,
      id: newOrderId,
      type: 'Order',
      client: offer.client,
      clientId: offer.clientId,
      date: getIsoDate(new Date()),
      status: 'New Order',
      overallStatus: 'In Progress',
      items: sourceItems.length,
      total: offer.total,
      internalTotal: offer.internalTotal,
      deliveryDate: offer.deliveryDate,
      sourceOfferId: offer.id,
      archived: false,
    };
    setOffers((prev) => [
      newOrder,
      ...prev.map((item) => (item.id === offer.id ? { ...item, status: 'Order Created', convertedOrderId: newOrderId } : item)),
    ]);
    setOrderSuppliers((prev) => ({
      ...prev,
      [newOrderId]: buildSupplierSectionsFromOfferItems(sourceItems),
    }));
    message.success(t('Order created: {{id}}', { id: newOrderId }));
    openOrderDetails(newOrder, 'offers');
  };

  // ── Offer — editable negotiation numbers on new-pipeline Offer_Items ─────
  const handleUpdateOfferItem = (offerId, itemId, field, value) => {
    const applyPatch = (record) => ({
      ...record,
      items: (record.items || []).map((item) => (item.id === itemId ? { ...item, [field]: value } : item)),
    });
    setOffers((prev) => prev.map((record) => (record.id === offerId ? applyPatch(record) : record)));
    setSelectedOrder((prev) => (prev?.id === offerId ? applyPatch(prev) : prev));
  };

  // ── Order — per-item fulfilment status & overall status ──────────────────
  const handleUpdateOrderItemStatus = (orderId, itemId, status) => {
    const applyPatch = (record) => ({
      ...record,
      items: (record.items || []).map((item) => (item.id === itemId ? { ...item, fulfilmentStatus: status } : item)),
    });
    setOffers((prev) => prev.map((record) => (record.id === orderId ? applyPatch(record) : record)));
    setSelectedOrder((prev) => (prev?.id === orderId ? applyPatch(prev) : prev));
  };

  const handleMarkOrderAccepted = (orderId) => {
    const order = offers.find((item) => item.id === orderId);
    if (!order || !allItemsAccepted(order)) return;
    setOffers((prev) => prev.map((item) => (item.id === orderId ? { ...item, overallStatus: 'Accepted by Customer' } : item)));
    setSelectedOrder((prev) => (prev?.id === orderId ? { ...prev, overallStatus: 'Accepted by Customer' } : prev));
    message.success(t('Order marked as Accepted by Customer'));
  };

  const handleSaveNewInquiryProduct = (productData) => {
    const productRecord = {
      id: products.length > 0 ? Math.max(...products.map((product) => product.id)) + 1 : 1,
      code: productData.code?.trim() || '',
      name: productData.name?.trim() || '',
      categoryId: productData.categoryId,
      description: productData.description?.trim() || '',
      productType: 'Standard',
      supplierId: suppliers.find((supplier) => supplier.name === productData.supplierRows?.[0]?.supplierName)?.id ?? null,
      supplierItemNumber: '',
      internalNumber: null,
      productFamily: '',
      copiedFromProductId: null,
      countryOfOrigin: null,
      packaging: null,
      specifications: null,
      materials: null,
      productionNotes: null,
      colour: '',
      primaryImageId: null,
      createdBy: currentUser?.name || '',
      createdAt: new Date().toISOString().slice(0, 16).replace('T', ' '),
      status: 'Active',
    };
    setProducts((prev) => [...prev, productRecord]);

    const newSupplierProducts = (productData.supplierRows || []).map((row, index) => ({
      id: supplierProducts.length > 0 ? Math.max(...supplierProducts.map((sp) => sp.id)) + index + 1 : index + 1,
      supplierId: suppliers.find((supplier) => supplier.name === row.supplierName)?.id,
      productId: productRecord.id,
      categoryId: productRecord.categoryId,
      productName: productRecord.name,
      supplierItemNumber: '',
      description: '',
      samplePhotoUrl: null,
      avgItemPrice: Number(row.avgItemPrice) || 0,
      avgProductionPrice: null,
      avgBrandingPrice: null,
      avgDeliveryPricePerItemPerKm: null,
      status: 'Active',
    }));
    setSupplierProducts((prev) => [...prev, ...newSupplierProducts]);

    message.success(t('Product created'));
    return productRecord;
  };

  const handleSupplierSubmit = (values) => {
    const supplierVisibility = canManageSupplierVisibility(currentUser) ? (values.visibility || 'Public') : 'Public';
    const supplierData = {
      id: editingSupplier
        ? editingSupplier.id
        : (suppliers.length > 0 ? Math.max(...suppliers.map((supplier) => supplier.id)) + 1 : 1),
      name: values.name?.trim() || '',
      visibility: supplierVisibility,
      supplierType: values.supplierType || editingSupplier?.supplierType || 'Product',
      cvr: values.cvr?.trim() || '',
      address: values.address?.trim() || '',
      country: values.country || editingSupplier?.country || 'Denmark',
      currency: values.currency || editingSupplier?.currency || 'DKK',
      phone: values.phone?.trim() || '',
      email: values.email?.trim() || '',
      website: values.website?.trim() || '',
      status: editingSupplier?.status || 'Active',
    };

    if (editingSupplier) {
      setSuppliers((prevSuppliers) => prevSuppliers.map((supplier) => (
        supplier.id === editingSupplier.id ? supplierData : supplier
      )));
      if (selectedSupplier?.id === supplierData.id) {
        setSelectedSupplier(supplierData);
      }
      message.success(t('Supplier updated'));
    } else {
      setSuppliers((prevSuppliers) => [...prevSuppliers, supplierData]);
      message.success(t('Supplier created'));
    }

    handleCancel();
  };

  const handleProductSubmit = (values) => {
    const productType = values.productType || editingProduct?.productType || 'Standard';
    const packaging = {
      unitPackaging: values.unitPackaging?.trim() || '',
      // Not editable via the product form anymore — preserve whatever was
      // there (seed data or a prior edit) rather than clearing it.
      itemsPerCarton: editingProduct?.packaging?.itemsPerCarton ?? null,
      cartonDimensionsCm: editingProduct?.packaging?.cartonDimensionsCm || '',
      cartonGrossWeightKg: editingProduct?.packaging?.cartonGrossWeightKg ?? null,
    };

    let supplierItemNumber = '';
    let internalNumber = editingProduct?.internalNumber || null;
    if (productType === 'Custom') {
      internalNumber = internalNumber || nextCustomProductNumber(products);
    } else {
      supplierItemNumber = values.supplierItemNumber?.trim() || '';
      if (!supplierItemNumber) {
        internalNumber = internalNumber || nextStandardFallbackNumber(products);
      }
    }

    const productId = editingProduct
      ? editingProduct.id
      : (products.length > 0 ? Math.max(...products.map((product) => product.id)) + 1 : 1);

    // If a new thumbnail was uploaded, reuse the existing primary-image
    // document's id (so it's overwritten in place) or allocate a new one.
    let primaryImageId = editingProduct?.primaryImageId ?? null;
    if (values.thumbnailFile) {
      const existingImageDoc = editingProduct
        ? productDocuments.find((doc) => doc.id === editingProduct.primaryImageId)
        : null;
      primaryImageId = existingImageDoc
        ? existingImageDoc.id
        : (productDocuments.length > 0 ? Math.max(...productDocuments.map((doc) => doc.id)) + 1 : 1);
    }

    const productData = {
      id: productId,
      code: editingProduct?.code || '',
      productType,
      name: values.name?.trim() || '',
      categoryId: values.categoryId,
      supplierId: values.supplierId,
      supplierItemNumber,
      internalNumber,
      productFamily: values.productFamily?.trim() || '',
      // Colour and production notes are no longer editable via this form
      // (colour was retired; production notes moved to their own tab) —
      // preserve whatever was already on the record.
      colour: editingProduct?.colour ?? '',
      description: values.description?.trim() || '',
      material: productType === 'Standard' ? (values.material?.trim() || '') : (editingProduct?.material ?? null),
      countryOfOrigin: productType === 'Standard' ? (values.countryOfOrigin?.trim() || '') : (editingProduct?.countryOfOrigin ?? null),
      specifications: productType === 'Custom' ? (values.specifications?.trim() || '') : (editingProduct?.specifications ?? null),
      materials: productType === 'Custom' ? (values.materials?.trim() || '') : (editingProduct?.materials ?? null),
      productionNotes: editingProduct?.productionNotes ?? null,
      packaging,
      primaryImageId,
      copiedFromProductId: editingProduct?.copiedFromProductId ?? null,
      createdBy: editingProduct?.createdBy || currentUser?.name || '',
      createdAt: editingProduct?.createdAt || new Date().toISOString().slice(0, 16).replace('T', ' '),
      status: editingProduct?.status || 'Active',
    };

    if (values.thumbnailFile) {
      const file = values.thumbnailFile;
      const fileUrl = URL.createObjectURL(file);
      const uploadStamp = new Date().toISOString().slice(0, 16).replace('T', ' ');
      setProductDocuments((prevDocs) => {
        const existingDoc = prevDocs.find((doc) => doc.id === primaryImageId);
        if (existingDoc) {
          return prevDocs.map((doc) => (
            doc.id === primaryImageId
              ? { ...doc, fileUrl, fileName: file.name, uploadedBy: currentUser?.name || '', uploadDate: uploadStamp }
              : doc
          ));
        }
        return [...prevDocs, {
          id: primaryImageId,
          productId: productData.id,
          documentType: 'Image',
          name: `${productData.name} — front view`,
          fileName: file.name,
          fileUrl,
          isPrimaryImage: true,
          inherited: false,
          uploadedBy: currentUser?.name || '',
          uploadDate: uploadStamp,
        }];
      });
    }

    if (editingProduct) {
      setProducts((prevProducts) => prevProducts.map((product) => (
        product.id === editingProduct.id ? productData : product
      )));
      if (selectedProduct?.id === productData.id) {
        setSelectedProduct(productData);
      }
      message.success(t('Product updated'));
    } else {
      setProducts((prevProducts) => [...prevProducts, productData]);
      message.success(t('Product created'));
    }

    // Keep the owning supplier's SupplierProduct row ("alternative sourcing")
    // in sync with the product's own average item price, so it's still
    // picked up by SupplierComparisonDialog without a separate manual step.
    // Computed outside the setState updater (rather than the prev-state form
    // used elsewhere) so the resulting row can also be handed back to a
    // wizard-mode caller (see wizardProductContext) for immediate selection.
    const existingSupplierProduct = supplierProducts.find((sp) => (
      sp.productId === productData.id && sp.supplierId === productData.supplierId
    ));
    let resultSupplierProduct = null;
    if (existingSupplierProduct || values.avgItemPrice != null) {
      resultSupplierProduct = existingSupplierProduct
        ? {
          ...existingSupplierProduct,
          avgItemPrice: values.avgItemPrice ?? null,
          productName: productData.name,
          categoryId: productData.categoryId,
          supplierItemNumber: productData.supplierItemNumber,
        }
        : {
          id: supplierProducts.length > 0 ? Math.max(...supplierProducts.map((sp) => sp.id)) + 1 : 1,
          supplierId: productData.supplierId,
          productId: productData.id,
          categoryId: productData.categoryId,
          productName: productData.name,
          supplierItemNumber: productData.supplierItemNumber,
          description: '',
          samplePhotoUrl: null,
          avgItemPrice: values.avgItemPrice,
          avgProductionPrice: null,
          avgBrandingPrice: null,
          avgDeliveryPricePerItemPerKm: null,
          status: 'Active',
        };
      const finalSupplierProduct = resultSupplierProduct;
      setSupplierProducts((prev) => (
        existingSupplierProduct
          ? prev.map((sp) => (sp.id === existingSupplierProduct.id ? finalSupplierProduct : sp))
          : [...prev, finalSupplierProduct]
      ));
    }

    if (wizardProductContext) {
      wizardProductContext.onReady?.(productData, resultSupplierProduct);
      setWizardProductContext(null);
      handleCancel();
      return;
    }

    handleCancel();
  };

  const handleSupplierProductSubmit = (values) => {
    const isEditing = Boolean(values.id);
    const supplierProductData = {
      ...values,
      id: isEditing
        ? values.id
        : (supplierProducts.length > 0 ? Math.max(...supplierProducts.map((sp) => sp.id)) + 1 : 1),
    };

    setSupplierProducts((prev) => (
      isEditing
        ? prev.map((sp) => (sp.id === supplierProductData.id ? supplierProductData : sp))
        : [...prev, supplierProductData]
    ));
    message.success(t(isEditing ? 'Supplier product updated' : 'Supplier product created'));
    handleCancel();
  };

  const handleDeleteSupplierProduct = (id) => {
    setSupplierProducts((prev) => prev.filter((sp) => sp.id !== id));
    message.success(t('Deleted'));
  };

  const handleOpenProductCreateChooser = () => {
    setEditingProduct(null);
    setCopySourceProduct(null);
    setWizardProductContext(null);
    showModal('productCreateChooser');
  };

  // Opened from a Category Details Selection Wizard's Products step
  // ("Add Product") — same chooser as Products Management, minus the Copy
  // Existing card (that's its own dedicated button there, see
  // openWizardCopyProduct), scoped to the item's category, and returning the
  // created product to the wizard instead of navigating away.
  const openWizardProductChooser = (categoryId, onReady) => {
    setEditingProduct(null);
    setCopySourceProduct(null);
    setWizardProductContext({ categoryId, onReady });
    showModal('productCreateChooser');
  };

  // Opened from a Category Details Selection Wizard's Products step
  // ("Copy Existing") — always produces a Custom product (a one-off for
  // this inquiry), regardless of the source product's own type.
  const openWizardCopyProduct = (categoryId, onReady) => {
    setCopySourceProduct(null);
    setWizardProductContext({ categoryId, onReady, forceCustom: true });
    showModal('copyProduct');
  };

  const handleCreateProductOfType = (type) => {
    setEditingProduct(null);
    setPendingProductType(type);
    showModal('createProduct');
  };

  const handleOpenCopyProduct = (sourceProduct = null) => {
    setCopySourceProduct(sourceProduct);
    setWizardProductContext(null);
    showModal('copyProduct');
  };

  const handleConfirmCopyProduct = (draft, sourceProduct) => {
    const newId = products.length > 0 ? Math.max(...products.map((product) => product.id)) + 1 : 1;
    // draft.avgItemPrice only exists in wizard mode (CopyProductModal's
    // requirePrice field, see wizardProductContext) — it isn't a Product field.
    const { avgItemPrice, ...productDraft } = draft;
    const newProduct = {
      ...productDraft,
      id: newId,
      status: 'Active',
      createdBy: currentUser?.name || '',
      createdAt: new Date().toISOString().slice(0, 16).replace('T', ' '),
    };

    setProducts((prev) => [...prev, newProduct]);

    const copiedDocuments = copyProductDocuments(productDocuments, sourceProduct.id, newId, newProduct.productType);
    if (copiedDocuments.length > 0) {
      setProductDocuments((prev) => {
        let nextId = prev.length > 0 ? Math.max(...prev.map((doc) => doc.id)) + 1 : 1;
        return [...prev, ...copiedDocuments.map((doc) => ({ ...doc, id: nextId++ }))];
      });
    }

    message.success(t('Copy created as {{number}}', {
      number: newProduct.productType === 'Custom' ? newProduct.internalNumber : (newProduct.supplierItemNumber || newProduct.internalNumber),
    }));

    if (wizardProductContext) {
      // A companion Supplier_Product row is created only here — a normal
      // (non-wizard) copy stays price-less, matching "prices are not copied
      // between customers"; the wizard collects a fresh price for this job
      // instead (requirePrice) specifically so the copy is immediately
      // selectable back on the Products step.
      const newSupplierProduct = {
        id: supplierProducts.length > 0 ? Math.max(...supplierProducts.map((sp) => sp.id)) + 1 : 1,
        supplierId: newProduct.supplierId,
        productId: newProduct.id,
        categoryId: newProduct.categoryId,
        productName: newProduct.name,
        supplierItemNumber: newProduct.supplierItemNumber || '',
        description: '',
        samplePhotoUrl: null,
        avgItemPrice: avgItemPrice ?? null,
        avgProductionPrice: null,
        avgBrandingPrice: null,
        avgDeliveryPricePerItemPerKm: null,
        status: 'Active',
      };
      setSupplierProducts((prev) => [...prev, newSupplierProduct]);
      wizardProductContext.onReady?.(newProduct, newSupplierProduct);
      setWizardProductContext(null);
      handleCancel();
      return;
    }

    setSelectedProduct(newProduct);
    setCurrentView('productDetails');
  };

  const handleAddProductDocument = () => {
    setEditingProductDocument(null);
    setIsProductDocumentModalOpen(true);
  };

  const handleEditProductDocument = (document) => {
    setEditingProductDocument(document);
    setIsProductDocumentModalOpen(true);
  };

  const handleProductDocumentSubmit = (documentData) => {
    const isEditing = Boolean(documentData.id);
    const finalDocument = {
      ...documentData,
      id: isEditing ? documentData.id : (productDocuments.length > 0 ? Math.max(...productDocuments.map((doc) => doc.id)) + 1 : 1),
    };

    setProductDocuments((prev) => (
      isEditing
        ? prev.map((doc) => (doc.id === finalDocument.id ? finalDocument : doc))
        : [...prev, finalDocument]
    ));

    if (finalDocument.isPrimaryImage) {
      setProducts((prev) => prev.map((product) => (
        product.id === finalDocument.productId ? { ...product, primaryImageId: finalDocument.id } : product
      )));
    }

    message.success(t(isEditing ? 'Document updated' : 'Document uploaded'));
    setIsProductDocumentModalOpen(false);
    setEditingProductDocument(null);
  };

  const handleDeleteProductDocument = (document) => {
    setProductDocuments((prev) => prev.filter((doc) => doc.id !== document.id));
    message.success(t('Deleted'));
  };

  const handleAddCertificate = (certData) => {
    if (certData.productId != null && !products.some((p) => p.id === certData.productId)) {
      message.error(t('Selected product could not be found'));
      return false;
    }
    const nextId = certificates.length > 0 ? Math.max(...certificates.map((c) => c.id)) + 1 : 1;
    const newCertificate = {
      ...certData,
      id: nextId,
      // Manually created certificates are never "inherited" — that flag is
      // only ever set by Copy Product (see copyProductCertificates).
      inherited: false,
      verifiedBy: null,
      verifiedAt: null,
      uploadedBy: currentUser?.name || 'System',
      uploadDate: new Date().toISOString().slice(0, 16).replace('T', ' '),
    };
    setCertificates((prev) => [...prev, newCertificate]);
    return true;
  };

  const handleEditCertificate = (certData) => {
    if (certData.productId != null && !products.some((p) => p.id === certData.productId)) {
      message.error(t('Selected product could not be found'));
      return false;
    }
    setCertificates((prev) => prev.map((cert) => (cert.id === certData.id ? { ...cert, ...certData } : cert)));
    return true;
  };

  const handleDeleteCertificate = (id) => {
    setCertificates((prev) => prev.filter((cert) => cert.id !== id));
  };

  const handleVerifyCertificate = (id) => {
    setCertificates((prev) => prev.map((cert) => (
      cert.id === id
        ? { ...cert, verifiedBy: currentUser?.name || 'System', verifiedAt: new Date().toISOString().slice(0, 10) }
        : cert
    )));
    message.success(t('Certificate verified'));
  };

  const handleAddProductionNote = (productId, noteText) => {
    const nextId = productionNotes.length > 0 ? Math.max(...productionNotes.map((n) => n.id)) + 1 : 1;
    const newNote = {
      id: nextId,
      productId,
      author: currentUser?.name || 'System',
      authorId: currentUser?.id ?? null,
      note: noteText,
      timestamp: new Date().toISOString().slice(0, 16).replace('T', ' '),
    };
    setProductionNotes((prev) => [...prev, newNote]);
    message.success(t('Note added'));
  };

  const handleAddDocument = (documentData) => {
    const nextId = documents.length > 0 ? Math.max(...documents.map((d) => d.id)) + 1 : 1;
    const newDocument = {
      ...documentData,
      id: nextId,
      uploadedBy: currentUser?.name || 'System',
      uploadDate: new Date().toISOString().slice(0, 16).replace('T', ' '),
    };
    setDocuments((prev) => [...prev, newDocument]);
    return true;
  };

  const handleEditDocument = (documentData) => {
    setDocuments((prev) => prev.map((doc) => (doc.id === documentData.id ? { ...doc, ...documentData } : doc)));
    return true;
  };

  const handleDeleteDocument = (id) => {
    setDocuments((prev) => prev.filter((doc) => doc.id !== id));
  };

  const handleRemoveItem = (itemId) => {
    setOfferItems(offerItems.filter(item => item.id !== itemId));
  };

  const calculateTotal = () => {
    return offerItems.reduce((sum, item) => sum + item.lineTotal, 0);
  };

  const calculateInternalTotal = () => {
    return offerItems.reduce((sum, item) => sum + (item.internalLineTotal ?? ((item.internalPrice || 0) * (item.quantity || 0))), 0);
  };

  const handleSaveContact = (values) => {
    const selectedCounterparty = leads.find((lead) => lead.id === values.clientId)
      || suppliers.find((supplier) => supplier.id === values.clientId);
    const source = values.type;
    const normalizedContact = {
      client: selectedCounterparty?.name || editingContact?.client || '',
      clientId: values.clientId,
      date: values.date ? values.date.format('YYYY-MM-DD') : '',
      type: source,
      subject: values.subject?.trim() || '',
      description: values.description?.trim() || '',
      referredBy: source === 'Referral'
        ? leads.find((lead) => lead.id === values.referredByClientId)?.name || ''
        : '',
      referredByClientId: source === 'Referral' ? values.referredByClientId || '' : '',
      eventName: source === 'Fair/Event' ? values.eventName?.trim() || '' : '',
      eventDate: source === 'Fair/Event' && values.eventDate ? values.eventDate.format('YYYY-MM-DD') : '',
      networkName: source === 'Network' ? values.networkName?.trim() || '' : '',
      userId: editingContact?.userId || currentUser?.id,
      userName: editingContact?.userName || currentUser?.name,
    };

    if (editingContact) {
      setContacts((prev) => prev.map((contact) => (
        contact.id === editingContact.id
          ? { ...contact, ...normalizedContact }
          : contact
      )));
      if (viewingContact?.id === editingContact.id) {
        setViewingContact((prev) => (prev ? { ...prev, ...normalizedContact } : prev));
      }
    } else {
      setContacts((prev) => [
        {
          ...normalizedContact,
          id: Date.now(),
        },
        ...prev,
      ]);
    }
  };

  const getClientComments = (clientId) => {
    return comments.filter(c => c.clientId === clientId);
  };

  // NEW: Helper to get latest comment for a client
  const getLatestComment = (clientId) => {
    const clientComments = getClientComments(clientId);
    return clientComments.length > 0 ? clientComments[0] : null;
  };

  // Helper function to get user name by ID
  const handleAddComment = (clientId, clientName, commentText) => {
    const newComment = {
      id: comments.length + 1,
      clientId,
      entityId: clientId,
      clientName,
      comment: commentText,
      author: currentUser.name,
      authorId: currentUser.id,
      timestamp: new Date().toISOString().slice(0, 16).replace('T', ' '),
    };
    setComments((prev) => [...prev, newComment]);
    message.success(t('Comment added successfully'));
  };

  const handleAddEntityComment = ({ entityId, entityName, commentText }) => {
    const newComment = {
      id: comments.length + 1,
      clientId: entityId,
      entityId,
      clientName: entityName,
      comment: commentText,
      author: currentUser.name,
      authorId: currentUser.id,
      timestamp: new Date().toISOString().slice(0, 16).replace('T', ' '),
    };
    setComments((prev) => [...prev, newComment]);

    const entityType = entityId.startsWith('INQ-') ? 'Inquiry' : entityId.startsWith('QUO-') ? 'Quote' : null;
    if (entityType) {
      appendNotifications(createCommentNotifications({
        entityType,
        entityId,
        entityLabel: entityName,
        authorId: currentUser.id,
        authorName: currentUser.name,
        users,
        currentMaxId: getCurrentMaxNotificationId(),
      }));
    }

    message.success(t('Comment added successfully'));
  };

  // ── Status override helpers ────────────────────────────────────────────────
  const getOfferStatus = (offerId, defaultStatus) => offerStatusOverrides[offerId] ?? defaultStatus;

  const updateOfferStatus = (offerId, newStatus) => {
    const record = offers.find((offer) => offer.id === offerId);
    setOfferStatusOverrides(prev => ({ ...prev, [offerId]: newStatus }));
    if (newStatus !== getOfferStatus(offerId, record?.status)) {
      appendNotifications(createStatusChangeNotifications({
        entityType: record?.type === 'Order' ? 'Order' : 'Offer',
        entityId: offerId,
        entityLabel: offerId,
        newStatus,
        actorId: currentUser?.id,
        users,
        currentMaxId: getCurrentMaxNotificationId(),
      }));
    }
    message.success(t('Status updated to: {{status}}', { status: t(newStatus) }));
    if (
      newStatus === 'Order Created'
      && record?.type === 'Offer'
      && getOfferStatus(offerId, record?.status) !== 'Order Created'
      && !record?.convertedOrderId
    ) {
      confirm({
        title: t('Create Order'),
        content: t('The Offer has reached the Order Created status. Would you like to create an Order based on this Internal Quote?'),
        okText: t('Yes'),
        cancelText: t('No'),
        onOk: () => handleCreateOrderFromOffer({ ...record, status: newStatus }),
      });
    }
  };

  const updateOfferPriority = (offerId, priority) => {
    const applyPatch = (record) => (record.id === offerId ? { ...record, priority } : record);
    setOffers((prev) => prev.map(applyPatch));
    setSelectedOrder((prev) => (prev?.id === offerId ? { ...prev, priority } : prev));
  };

  // ── Follow-up helpers ──────────────────────────────────────────────────────
  const getFollowUpsForOffer = (offerId) => followUps.filter(f => f.offerId === offerId);

  const getOverdueCount = (offerId) =>
    followUps.filter(f => f.offerId === offerId && f.reminder === 'Overdue!').length;

  const getFollowUpComments = (followUp) => {
    if (Array.isArray(followUp?.comments)) return followUp.comments;
    if (followUp?.notes) {
      return [{
        id: `${followUp.id}-legacy-note`,
        comment: followUp.notes,
        author: followUp.responsible || currentUser?.name || 'System',
        timestamp: followUp.date,
      }];
    }
    return [];
  };

  const handleAddFollowUpComment = (followUpId, commentText) => {
    const trimmed = commentText.trim();
    if (!trimmed) {
      message.warning(t('Please enter a comment'));
      return null;
    }
    const newComment = {
      id: `${followUpId}-${Date.now()}`,
      comment: trimmed,
      author: currentUser?.name || 'System',
      timestamp: new Date().toISOString().slice(0, 16).replace('T', ' '),
    };

    setFollowUps((prev) => prev.map((item) => {
      if (item.id !== followUpId) return item;
      const existingComments = getFollowUpComments(item);
      return {
        ...item,
        comments: [
          ...existingComments,
          newComment,
        ],
      };
    }));

    setNewFollowUpComment('');
    message.success(t('Comment added'));
    return newComment;
  };

  // Login page
  if (!isLoggedIn) {
    return (
      <I18nProvider language={language} setLanguage={setLanguage}>
        <ConfigProvider theme={theme} locale={getAntdLocale(language)}>
          <LoginPage handleLogin={handleLogin} />
        </ConfigProvider>
      </I18nProvider>
    );
  }

  // NEW: File management functions
  const handleUploadFile = (clientId, file, description) => {
    const newFile = {
      id: files.length + 1,
      clientId: clientId,
      fileName: file.name,
      fileType: file.name.split('.').pop().toLowerCase(),
      description: description || 'Uploaded file',
      uploadDate: new Date().toISOString().slice(0, 16).replace('T', ' '),
      uploadedBy: currentUser?.name || 'System',
      generatedFromOffer: false,
    };
    
    setFiles([...files, newFile]);
    message.success(t('File uploaded successfully'));
    return false; // Prevent default upload behavior
  };

  const handleDeleteFile = (fileId) => {
    if (!currentUserCanDelete) {
      message.warning(t('You do not have permission to delete records'));
      return;
    }
    confirm({
      title: t('Delete File'),
      content: t('Are you sure you want to delete this file?'),
      okText: t('Delete'),
      okType: 'danger',
      onOk() {
        setFiles(files.filter(f => f.id !== fileId));
        message.success(t('File deleted successfully'));
      },
    });
  };

  const handleEditFileDescription = (fileId, newDescription) => {
    setFiles(files.map(f => 
      f.id === fileId ? { ...f, description: newDescription } : f
    ));
    message.success(t('Description updated'));
  };

  const handlePreviewPDF = (file) => {
    message.info(t('Opening PDF preview for: {{fileName}}', { fileName: file.fileName }));
    // In real implementation, this would open a PDF viewer modal
  };

  const handleDownloadFile = (file) => {
    if (file.objectUrl) {
      const link = document.createElement('a');
      link.href = file.objectUrl;
      link.download = file.fileName;
      link.click();
      return;
    }
    message.info(t('Demo file download: {{fileName}}', { fileName: file.fileName }));
  };

  // Modal renderer
  const renderModal = () => {
    if (modalType === 'viewContact' && viewingContact) {
      return (
        <ContactDetailsModal
          open={isModalVisible}
          onCancel={handleCancel}
          contact={viewingContact}
          t={t}
          onEdit={() => {
            setEditingContact(viewingContact);
            setModalType('editContact');
          }}
        />
      );
    }

    if (modalType === 'viewComments' && viewingComments) {
      return (
        <CommentsModal
          open={isModalVisible}
          onCancel={handleCancel}
          viewingComments={viewingComments}
          comments={getClientComments(viewingComments.clientId)}
          newComment={newComment}
          setNewComment={setNewComment}
          onAddComment={(clientId, clientName, commentText) => {
            const isInquiryComment = inquiries.some((inquiry) => inquiry.id === clientId);
            if (isInquiryComment) {
              handleAddEntityComment({ entityId: clientId, entityName: clientName, commentText });
              return;
            }
            handleAddComment(clientId, clientName, commentText);
          }}
          t={t}
        />
      );
    }

    if (modalType === 'selectCounterpartyType') {
      return (
        <CounterpartyTypeModal
          open={isModalVisible}
          onCancel={handleCancel}
          onSelect={(type) => {
            setModalType(type === 'client' ? 'createLead' : 'createSupplier');
          }}
        />
      );
    }

    // Create/Edit lead
    if (modalType === 'createLead' || (modalType === 'editLead' && editingLead)) {
      return (
        <LeadModal
          modalType={modalType}
          open={isModalVisible}
          onCancel={handleCancel}
          onSubmit={handleLeadSubmit}
          form={leadForm}
        />
      );
    }

    if (modalType === 'createUser' || (modalType === 'editUser' && editingUser)) {
      return (
        <Modal
          title={modalType === 'createUser' ? 'Add User' : 'Edit User'}
          open={isModalVisible}
          onCancel={handleCancel}
          footer={[
            <Button key="cancel" onClick={handleCancel}>Cancel</Button>,
            <Button key="submit" type="primary" onClick={handleUserSubmit}>
              {modalType === 'createUser' ? 'Create' : 'Update'}
            </Button>,
          ]}
          width={720}
        >
          <Form form={userForm} layout="vertical">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="First Name"
                  name="firstName"
                  rules={[{ required: true, message: 'Please enter first name' }]}
                >
                  <Input placeholder="First name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Last Name"
                  name="lastName"
                  rules={[{ required: true, message: 'Please enter last name' }]}
                >
                  <Input placeholder="Last name" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                    { required: true, message: 'Please enter email address' },
                    { type: 'email', message: 'Please enter a valid email address' },
                  ]}
                >
                  <Input placeholder="email@example.com" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Phone"
                  name="phone"
                  rules={[{ required: true, message: 'Please enter phone number' }]}
                >
                  <Input placeholder="+45 12 34 56 78" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Login"
                  name="login"
                  rules={[{ required: true, message: 'Please enter login' }]}
                >
                  <Input placeholder="User login" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Password"
                  name="password"
                  rules={[{ required: true, message: 'Please enter password' }]}
                >
                  <Input.Password placeholder="Password" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Role"
                  name="role"
                  rules={[{ required: true, message: 'Please select role' }]}
                >
                  <Select
                    options={[
                      { value: 'procurement manager', label: 'procurement manager' },
                      { value: 'procurement user', label: 'procurement user' },
                      { value: 'sales manager', label: 'sales manager' },
                      { value: 'sales user', label: 'sales user' },
                      { value: 'admin', label: 'admin' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Preferred Language"
                  name="preferredLanguage"
                  rules={[{ required: true, message: 'Please select preferred language' }]}
                >
                  <Select
                    options={[
                      { value: 'en', label: 'English' },
                      { value: 'da', label: 'Danish' },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              label="Status"
              name="active"
              rules={[{ required: true, message: 'Please select status' }]}
            >
              <Select
                options={[
                  { value: true, label: 'Active' },
                  { value: false, label: 'Inactive' },
                ]}
              />
            </Form.Item>
          </Form>
        </Modal>
      );
    }

    // Create/Edit contact
    if (modalType === 'createContact' || (modalType === 'editContact' && editingContact)) {
      return (
        <ContactModal
          modalType={modalType}
          isModalVisible={isModalVisible}
          handleCancel={handleCancel}
          editingContact={editingContact}
          currentView={currentView}
          selectedLead={selectedLead}
          selectedSupplier={selectedSupplier}
          leads={leads}
          suppliers={visibleSuppliers}
          onSave={handleSaveContact}
        />
      );
    }

    if (modalType === 'createContactPerson' || modalType === 'editContactPerson') {
      return (
        <ContactPersonModal
          open={isModalVisible}
          onCancel={handleCancel}
          onSubmit={handleContactPersonSubmit}
          editingContactPerson={editingContactPerson}
        />
      );
    }

    // Create Offer/Order
    if (modalType === 'createOfferOrder' || modalType === 'createOffer' || modalType === 'createOrder' || modalType === 'repeatOffer') {
      return (
        <OfferOrderModal
          modalType={modalType}
          isModalVisible={isModalVisible}
          handleCancel={handleCancel}
          currentView={currentView}
          selectedLead={selectedLead}
          leads={leads}
          products={visibleProducts}
          categories={productCategories}
          services={serviceItems}
          logisticsItems={logisticsItems}
          suppliers={visibleSuppliers}
          supplierProducts={visibleSupplierProducts}
          editingOffer={repeatingOffer}
          isRepeat={modalType === 'repeatOffer'}
          offerItems={offerItems}
          setOfferItems={setOfferItems}
          handleRemoveItem={handleRemoveItem}
          calculateTotal={calculateTotal}
          calculateInternalTotal={calculateInternalTotal}
          onCreateCounterparty={handleCreateCounterparty}
          onSave={handleSaveOfferOrder}
          canDelete={currentUserCanDelete}
        />
      );
    }

    // Edit offer/order
    if (modalType === 'editOffer' && editingOffer) {
      return (
        <OfferOrderModal
          modalType={modalType}
          isModalVisible={isModalVisible}
          handleCancel={handleCancel}
          currentView={currentView}
          selectedLead={selectedLead}
          leads={leads}
          products={visibleProducts}
          categories={productCategories}
          services={serviceItems}
          logisticsItems={logisticsItems}
          suppliers={visibleSuppliers}
          supplierProducts={visibleSupplierProducts}
          editingOffer={editingOffer}
          offerItems={offerItems}
          setOfferItems={setOfferItems}
          handleRemoveItem={handleRemoveItem}
          calculateTotal={calculateTotal}
          calculateInternalTotal={calculateInternalTotal}
          onCreateCounterparty={handleCreateCounterparty}
          onSave={handleSaveOfferOrder}
          canDelete={currentUserCanDelete}
        />
      );
    }

    // Create/Edit supplier
    if (modalType === 'createSupplier' || modalType === 'editSupplier') {
      return (
        <SupplierModal
          open={isModalVisible}
          onCancel={handleCancel}
          onSubmit={handleSupplierSubmit}
          editingSupplier={editingSupplier}
          currentUser={currentUser}
        />
      );
    }

    // Choose Create New (Standard/Custom) vs Copy Existing
    if (modalType === 'productCreateChooser') {
      return (
        <ProductCreateChooserModal
          open={isModalVisible}
          onCancel={handleCancel}
          onCreateNew={(type) => handleCreateProductOfType(type)}
          onCopyExisting={() => handleOpenCopyProduct(null)}
          hideCopyOption={Boolean(wizardProductContext)}
        />
      );
    }

    // Copy Product (source picker or preselected + guided edit + confirmation)
    if (modalType === 'copyProduct') {
      return (
        <CopyProductModal
          open={isModalVisible}
          onCancel={handleCancel}
          onConfirm={handleConfirmCopyProduct}
          products={visibleProducts}
          suppliers={visibleSuppliers}
          categories={productCategories}
          initialSourceProduct={copySourceProduct}
          forceProductType={wizardProductContext?.forceCustom ? 'Custom' : undefined}
          lockedCategoryId={wizardProductContext?.categoryId}
          requirePrice={Boolean(wizardProductContext)}
        />
      );
    }

    // Create/Edit product
    if (modalType === 'createProduct' || modalType === 'editProduct') {
      return (
        <ProductModal
          open={isModalVisible}
          onCancel={handleCancel}
          onSubmit={handleProductSubmit}
          editingProduct={editingProduct}
          categories={productCategories}
          suppliers={visibleSuppliers}
          supplierProducts={supplierProducts}
          documents={productDocuments}
          productType={pendingProductType}
          lockedCategoryId={wizardProductContext?.categoryId}
          requirePrice={Boolean(wizardProductContext)}
        />
      );
    }

    // Create/Edit supplier product
    if (modalType === 'createSupplierProduct' || modalType === 'editSupplierProduct') {
      return (
        <SupplierProductModal
          open={isModalVisible}
          onCancel={handleCancel}
          onSubmit={handleSupplierProductSubmit}
          editingSupplierProduct={editingSupplierProduct}
          supplier={selectedSupplier}
          categories={productCategories}
          products={visibleProducts}
        />
      );
    }

    if (modalType === 'viewFollowUps' && viewingFollowUps) {
      return (
        <FollowUpsModal
          open={isModalVisible}
          onCancel={handleCancel}
          viewingFollowUps={viewingFollowUps}
          currentUser={currentUser}
          users={users}
          items={getFollowUpsForOffer(viewingFollowUps.id)}
          setFollowUps={setFollowUps}
          addingFollowUp={addingFollowUp}
          setAddingFollowUp={setAddingFollowUp}
          editingFollowUpId={editingFollowUpId}
          setEditingFollowUpId={setEditingFollowUpId}
          newFollowUp={newFollowUp}
          setNewFollowUp={setNewFollowUp}
          viewingFollowUpComments={viewingFollowUpComments}
          setViewingFollowUpComments={setViewingFollowUpComments}
          newFollowUpComment={newFollowUpComment}
          setNewFollowUpComment={setNewFollowUpComment}
          getFollowUpComments={getFollowUpComments}
          handleAddFollowUpComment={handleAddFollowUpComment}
          t={t}
          canDelete={currentUserCanDelete}
        />
      );
    }

    // ── Supplier line items modal ───────────────────────────────────────────
    if (modalType === 'viewSuppliers' && viewingSuppliers) {
      const sups = maskPrivateSupplierSections(getOfferSupplierSections(viewingSuppliers.id));
      const grandTotal = sups.reduce((sum, s) => sum + s.items.reduce((a, i) => a + i.total, 0), 0);
      const supStatusTone = {
        Completed: { color: brandSemantic.success, background: brandSemantic.successSoft, border: brandSemantic.successBorder },
        Production: { color: brandSemantic.warning, background: brandSemantic.warningSoft, border: brandSemantic.warningBorder },
        'Proof Pending': { color: brandSemantic.info, background: brandSemantic.infoSoft, border: brandSemantic.infoBorder },
        Pending: { color: brandSemantic.textSecondary, background: brandSemantic.primarySoft, border: brandSemantic.primaryMuted },
      };
      const getSupplierStatusSelectStyle = (status) => ({
        width: Math.min(Math.max(status.length * 7 + 34, 108), 152),
        '--status-color': (supStatusTone[status] || supStatusTone.Pending).color,
        '--status-bg': (supStatusTone[status] || supStatusTone.Pending).background,
        '--status-border': (supStatusTone[status] || supStatusTone.Pending).border,
      });

      return (
        <Modal
          title={
            <Space>
              <FileOutlined style={{ color: brandSemantic.primary, fontSize: 16 }} />
              <strong>Line Items — Split by Supplier</strong>
              <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>{viewingSuppliers.id} · {viewingSuppliers.client}</Text>
            </Space>
          }
          open={isModalVisible}
          onCancel={handleCancel}
          footer={null}
          width={920}
        >
          {sups.length === 0 ? (
            <Empty description="No supplier line items for this offer/order" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <Space direction="vertical" style={{ width: '100%' }} size={16}>
              {sups.map((sup, idx) => {
                const supTotal = sup.items.reduce((a, i) => a + i.total, 0);
                const supplierStatusKey = `${viewingSuppliers.id}-${sup.number}`;
                const currentSupplierStatus = supplierStatusOverrides[supplierStatusKey] || sup.status;
                return (
                  <Card
                    key={idx}
                    size="small"
                    style={{ border: `1px solid ${brandSemantic.border}`, borderRadius: 10 }}
                    title={
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <FileOutlined style={{ color: brandSemantic.primary, fontSize: 16 }} />
                        <strong style={{ fontSize: 15 }}>{sup.displayName || sup.name}</strong>
                        <Tag color="blue" style={{ fontSize: 11 }}>Supplier #{sup.number}</Tag>
                        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12, paddingRight: 8 }}>
                          <span>Status:</span>
                          <Select
                            value={currentSupplierStatus}
                            onChange={(value) => {
                              setSupplierStatusOverrides((prev) => ({
                                ...prev,
                                [supplierStatusKey]: value,
                              }));
                            }}
                            size="small"
                            variant="borderless"
                            className="status-tag-select"
                            style={getSupplierStatusSelectStyle(currentSupplierStatus)}
                            dropdownMatchSelectWidth={false}
                            options={[
                              { value: 'Pending', label: 'Pending' },
                              { value: 'Proof Pending', label: 'Proof Pending' },
                              { value: 'Production', label: 'Production' },
                              { value: 'Completed', label: 'Completed' },
                            ]}
                          />
                        </span>
                      </div>
                    }
                    extra={<strong style={{ fontSize: 15, marginLeft: 12, display: 'block' }}>{supTotal.toLocaleString('da-DK')} DKK</strong>}
                  >
                    <Table
                      className="supplier-split-table"
                      dataSource={sup.items}
                      rowKey="row"
                      pagination={false}
                      size="small"
                      style={{ marginBottom: 8 }}
                      tableLayout="fixed"
                      scroll={{ x: 950 }}
                      columns={[
                        { title: '#', dataIndex: 'row', key: 'row', width: 40 },
                        { title: 'Description', dataIndex: 'description', key: 'description', width: 250, ellipsis: true },
                        {
                          title: 'Notes',
                          dataIndex: 'notes',
                          key: 'notes',
                          width: 220,
                          ellipsis: true,
                          render: (value) => value || <span style={{ color: '#bfbfbf' }}>—</span>,
                        },
                        { title: 'Qty', dataIndex: 'qty', key: 'qty', width: 70, align: 'right', render: v => v.toLocaleString('da-DK') },
                        {
                          title: 'Internal cost',
                          key: 'internalCost',
                          width: 115,
                          align: 'right',
                          render: (_, item) => Number(item.internalPrice ?? item.costFromSupplier ?? 0).toLocaleString('da-DK'),
                        },
                        {
                          title: 'Customer price',
                          key: 'customerPrice',
                          width: 120,
                          align: 'right',
                          render: (_, item) => Number(item.customerPrice ?? item.unitPrice ?? 0).toLocaleString('da-DK'),
                        },
                        {
                          title: 'Discount %',
                          dataIndex: 'discount',
                          key: 'discount',
                          width: 95,
                          align: 'right',
                          render: (value) => `${Number(value || 0).toLocaleString('da-DK')}%`,
                        },
                        {
                          title: 'Internal total',
                          key: 'internalLineTotal',
                          width: 120,
                          align: 'right',
                          render: (_, item) => {
                            const total = item.internalLineTotal ?? (Number(item.internalPrice ?? item.costFromSupplier ?? 0) * Number(item.qty || 0));
                            return <strong>{Number(total || 0).toLocaleString('da-DK')}</strong>;
                          },
                        },
                        {
                          title: 'Customer total',
                          key: 'customerLineTotal',
                          width: 120,
                          align: 'right',
                          render: (_, item) => {
                            const total = item.total ?? (
                              Number(item.customerPrice ?? item.unitPrice ?? 0)
                              * Number(item.qty || 0)
                              * (1 - (Number(item.discount) || 0) / 100)
                            );
                            return <strong>{Number(total || 0).toLocaleString('da-DK')}</strong>;
                          },
                        },
                      ]}
                    />
                    <div style={{ textAlign: 'right', padding: '8px 0', borderTop: `1px solid ${brandSemantic.borderSecondary}`, color: brandSemantic.textSecondary, fontSize: 13 }}>
                      Subtotal — {(sup.displayName || sup.name).split(' ')[0]}: <strong style={{ color: '#000', fontSize: 14 }}>{supTotal.toLocaleString('da-DK')}</strong>
                    </div>
                  </Card>
                );
              })}

              <div style={{ padding: '14px 20px', background: brandSemantic.layout, borderRadius: 10, border: `1px solid ${brandSemantic.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text style={{ fontSize: 15, fontWeight: 600 }}>Grand Total — Order value to client</Text>
                <Text style={{ fontSize: 20, fontWeight: 700 }}>{grandTotal.toLocaleString('da-DK')} DKK</Text>
              </div>
            </Space>
          )}
        </Modal>
      );
    }
  };

  return (
    <I18nProvider language={language} setLanguage={setLanguage}>
      <ConfigProvider theme={theme} locale={getAntdLocale(language)}>
      <style>{sidebarStyles}</style>
      <Layout style={{ minHeight: '100vh', overflow: 'hidden' }}>
        <AppSidebar
          currentView={currentView}
          setCurrentView={setCurrentView}
          setSelectedLead={setSelectedLead}
          leadProfileReturnView={leadProfileReturnView}
          orderDetailsReturn={orderDetailsReturn}
          inquiryReturnView={inquiryReturnView}
          quoteReturnView={quoteReturnView}
        />

        <Layout style={{ marginLeft: 104, minHeight: '100vh', height: '100vh', overflow: 'hidden' }}>
          <AppHeader
            currentUser={currentUser}
            language={language}
            setLanguage={setLanguage}
            colorPalette={colorPalette}
            setColorPalette={setColorPalette}
            setCurrentView={setCurrentView}
            handleLogout={handleLogout}
            notifications={notifications}
            onMarkNotificationRead={handleMarkNotificationRead}
            onMarkAllNotificationsRead={handleMarkAllNotificationsRead}
            onNotificationNavigate={handleNotificationNavigate}
          />

          <Content
            style={{
              marginTop: 64,
              padding: '24px',
              background: brandSemantic.layout,
              height: 'calc(100vh - 64px)',
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            {currentView === 'start' && (
              <StartPage
                setCurrentView={setCurrentView}
                showModal={showModal}
              />
            )}
            {currentView === 'leads' && (
              <LeadsListPage
                scope="customers"
                leads={leads}
                suppliers={[]}
                getFilteredLeads={getFilteredLeads}
                getClientComments={getClientComments}
                clientTypeFilter={leadTypeFilter}
                setClientTypeFilter={setLeadTypeFilter}
                searchText={leadSearchText}
                setSearchText={setLeadSearchText}
                openLeadProfile={openLeadProfile}
                openSupplierProfile={openSupplierProfile}
                showModal={showModal}
                setEditingSupplier={setEditingSupplier}
                canDelete={currentUserCanDelete}
              />
            )}
            {currentView === 'suppliers' && (
              <LeadsListPage
                scope="suppliers"
                leads={[]}
                suppliers={canViewSuppliersInCounterpartyList(currentUser) ? visibleSuppliers : []}
                supplierProducts={supplierProducts}
                services={serviceItems}
                logisticsItems={logisticsItems}
                getFilteredLeads={() => []}
                getClientComments={getClientComments}
                clientTypeFilter="all"
                setClientTypeFilter={() => {}}
                searchText={supplierSearchText}
                setSearchText={setSupplierSearchText}
                openLeadProfile={openLeadProfile}
                openSupplierProfile={openSupplierProfile}
                showModal={showModal}
                setEditingSupplier={setEditingSupplier}
                canDelete={currentUserCanDelete}
              />
            )}
            {currentView === 'leadProfile' && (
              <LeadProfilePage
                selectedLead={selectedLead}
                leadProfileReturnView={leadProfileReturnView}
                leadProfileActiveTab={leadProfileActiveTab}
                setLeadProfileActiveTab={setLeadProfileActiveTab}
                contacts={contacts}
                contactPersons={contactPersons}
                offers={offers}
                inquiries={inquiries}
                quotes={quotes}
                files={files}
                showModal={showModal}
                setSelectedLead={setSelectedLead}
                setCurrentView={setCurrentView}
                openOrderDetails={openOrderDetails}
                openQuoteDetail={(quote) => openQuoteDetail(quote, 'leadProfile')}
                openInquiryDetail={(inquiry) => openInquiryDetail(inquiry, 'leadProfile')}
                onCreateInquiry={() => openInquiryCreate('leadProfile', selectedLead?.id)}
                getOfferSupplierSections={getOfferSupplierSections}
                getFollowUpsForOffer={getFollowUpsForOffer}
                getOverdueCount={getOverdueCount}
                getOfferStatus={getOfferStatus}
                updateOfferStatus={updateOfferStatus}
                updateOfferPriority={updateOfferPriority}
                getLatestComment={getLatestComment}
                openCommentsModal={openCommentsModal}
                handleUploadFile={handleUploadFile}
                handleDeleteFile={handleDeleteFile}
                handleEditFileDescription={handleEditFileDescription}
                handlePreviewPDF={handlePreviewPDF}
                handleDownloadFile={handleDownloadFile}
                canDelete={currentUserCanDelete}
              />
            )}
            {currentView === 'contacts' && (
              <ContactsListPage
                contacts={contacts}
                currentUser={currentUser}
                contactFilter={contactFilter}
                setContactFilter={setContactFilter}
                contactTypeFilter={contactTypeFilter}
                setContactTypeFilter={setContactTypeFilter}
                contactDateRange={contactDateRange}
                setContactDateRange={setContactDateRange}
                leads={leads}
                suppliers={visibleSuppliers}
                openLeadProfile={openLeadProfile}
                openSupplierProfile={openSupplierProfile}
                showModal={showModal}
                canDelete={currentUserCanDelete}
              />
            )}
            {currentView === 'supplierProfile' && (
              <SupplierProfilePage
                selectedSupplier={selectedSupplier}
                returnView={supplierProfileReturnView}
                contacts={contacts}
                contactPersons={contactPersons}
                supplierProducts={visibleSupplierProducts}
                services={serviceItems}
                logisticsItems={logisticsItems}
                offers={offers}
                quotes={quotes}
                leads={leads}
                certificates={certificates}
                onAddCertificate={handleAddCertificate}
                onEditCertificate={handleEditCertificate}
                onDeleteCertificate={handleDeleteCertificate}
                documents={documents}
                onAddDocument={handleAddDocument}
                onEditDocument={handleEditDocument}
                onDeleteDocument={handleDeleteDocument}
                products={visibleProducts}
                categories={productCategories}
                currentUser={currentUser}
                showModal={showModal}
                setSelectedSupplier={setSelectedSupplier}
                setCurrentView={setCurrentView}
                openOrderDetails={openOrderDetails}
                openQuoteDetail={(quote) => openQuoteDetail(quote, 'supplierProfile')}
                openLeadProfile={openLeadProfile}
                getOfferSupplierSections={getOfferSupplierSections}
                getFollowUpsForOffer={getFollowUpsForOffer}
                getOverdueCount={getOverdueCount}
                getOfferStatus={getOfferStatus}
                updateOfferStatus={updateOfferStatus}
                getLatestComment={getLatestComment}
                openCommentsModal={openCommentsModal}
                setEditingSupplier={setEditingSupplier}
                setEditingSupplierProduct={setEditingSupplierProduct}
                onDeleteSupplierProduct={handleDeleteSupplierProduct}
                canDelete={currentUserCanDelete}
              />
            )}
            {currentView === 'inquiries' && (
              <InquiriesPage
                inquiries={inquiries}
                leads={leads}
                categories={productCategories}
                openInquiryDetail={openInquiryDetail}
                openLeadProfile={openLeadProfile}
                isSalesUser={currentUserIsSales}
                onCreateInquiry={() => openInquiryCreate()}
                canDelete={currentUserCanDelete}
              />
            )}
            {currentView === 'inquiryCreate' && (
              <InquiryCreatePage
                leads={leads}
                categories={productCategories}
                products={visibleProducts}
                services={serviceItems}
                logisticsItems={logisticsItems}
                users={users}
                currentUser={currentUser}
                initialClientId={inquiryCreateClientId}
                onSave={(inquiryData) => {
                  const savedInquiry = handleSaveInquiry(inquiryData);
                  openInquiryDetail(savedInquiry, inquiryCreateReturnView || 'inquiries');
                }}
                onCancel={returnFromInquiryCreate}
                showModal={showModal}
              />
            )}
            {currentView === 'quotes' && (
              <QuotesPage
                quotes={quotes}
                leads={leads}
                openQuoteDetail={openQuoteDetail}
                openLeadProfile={openLeadProfile}
                canDelete={currentUserCanDelete}
              />
            )}
            {currentView === 'offers' && (
              <OrdersPage
                offers={offers}
                inquiries={inquiries}
                quotes={quotes}
                suppliers={visibleSuppliers}
                leads={leads}
                offerStatusFilter={offerStatusFilter}
                setOfferStatusFilter={setOfferStatusFilter}
                getOfferStatus={getOfferStatus}
                updateOfferStatus={updateOfferStatus}
                getFollowUpsForOffer={getFollowUpsForOffer}
                getOverdueCount={getOverdueCount}
                getOfferSupplierSections={getOfferSupplierSections}
                showModal={showModal}
                openLeadProfile={openLeadProfile}
                setCurrentView={setCurrentView}
                openOrderDetails={openOrderDetails}
                openInquiryDetail={openInquiryDetail}
                openQuoteDetail={openQuoteDetail}
                canDelete={currentUserCanDelete}
                typeFilter="Offer"
                title="Sales Quotes"
                returnView="offers"
                showCreateOfferOrder={false}
              />
            )}
            {currentView === 'orders' && (
              <OrdersPage
                offers={offers}
                inquiries={inquiries}
                quotes={quotes}
                suppliers={visibleSuppliers}
                leads={leads}
                offerStatusFilter={offerStatusFilter}
                setOfferStatusFilter={setOfferStatusFilter}
                getOfferStatus={getOfferStatus}
                updateOfferStatus={updateOfferStatus}
                getFollowUpsForOffer={getFollowUpsForOffer}
                getOverdueCount={getOverdueCount}
                getOfferSupplierSections={getOfferSupplierSections}
                showModal={showModal}
                openLeadProfile={openLeadProfile}
                setCurrentView={setCurrentView}
                openOrderDetails={openOrderDetails}
                openInquiryDetail={openInquiryDetail}
                openQuoteDetail={openQuoteDetail}
                canDelete={currentUserCanDelete}
                typeFilter="Order"
                title="Orders"
                returnView="orders"
                showCreateOfferOrder={false}
              />
            )}
            {currentView === 'compliance' && (
              <CompliancePage
                certificates={certificates}
                suppliers={suppliers}
                products={visibleProducts}
                openSupplierProfile={openSupplierProfile}
                onOpenProduct={openProductDetails}
                onVerifyCertificate={handleVerifyCertificate}
              />
            )}
            {currentView === 'followUps' && (
              <FollowUpsPage
                followUps={followUps}
                setFollowUps={setFollowUps}
                offers={offers}
                leads={leads}
                users={users}
                currentUser={currentUser}
                openOrderDetails={openOrderDetails}
                openLeadProfile={openLeadProfile}
                getFollowUpComments={getFollowUpComments}
                handleAddFollowUpComment={handleAddFollowUpComment}
                canDelete={currentUserCanDelete}
              />
            )}
            {currentView === 'orderDetails' && selectedOrder && (
              <EntityDetailsPage
                order={{
                  ...selectedOrder,
                  createdBy: selectedOrder.id === 'DAN-20260128' ? 'Mads Mark-Buur' : currentUser?.name,
                }}
                lead={leads.find((lead) => lead.id === selectedOrder.clientId)}
                suppliers={maskPrivateSupplierSections(getOfferSupplierSections(selectedOrder.id))}
                supplierDirectory={suppliers}
                quotes={quotes}
                offers={offers}
                followUps={followUps.filter((item) => item.offerId === selectedOrder.id)}
                files={files.filter((file) => file.offerId === selectedOrder.id)}
                certificates={certificates}
                setFollowUps={setFollowUps}
                currentUser={currentUser}
                users={users}
                getFollowUpComments={getFollowUpComments}
                handleAddFollowUpComment={handleAddFollowUpComment}
                getOfferStatus={getOfferStatus}
                updateOfferStatus={updateOfferStatus}
                updateOfferPriority={updateOfferPriority}
                showModal={showModal}
                onBack={returnFromOrderDetails}
                onConvertToOrder={(offer) => (
                  Array.isArray(offer.items) && offer.items.length > 0
                    ? handleCreateOrderFromOffer(offer)
                    : handleConvertOfferToOrder(offer)
                )}
                onOpenSourceOffer={(offerId) => {
                  const offer = offers.find((item) => item.id === offerId);
                  if (offer) openOrderDetails(offer, 'offers');
                }}
                onOpenSourceQuote={(quoteId) => {
                  const quote = quotes.find((item) => item.id === quoteId);
                  if (quote) openQuoteDetail(quote, 'orderDetails');
                }}
                onOpenConvertedOrder={(orderId) => {
                  const orderRecord = offers.find((item) => item.id === orderId);
                  if (orderRecord) openOrderDetails(orderRecord, 'orders');
                }}
                onDownloadFile={handleDownloadFile}
                onUpdateOrderItemStatus={(itemId, status) => handleUpdateOrderItemStatus(selectedOrder.id, itemId, status)}
                onMarkOrderAccepted={() => handleMarkOrderAccepted(selectedOrder.id)}
                onUpdateOfferItem={(itemId, field, value) => handleUpdateOfferItem(selectedOrder.id, itemId, field, value)}
                t={t}
                canDelete={currentUserCanDelete}
              />
            )}
            {currentView === 'inquiryDetail' && selectedInquiry && (
              <InquiryDetailPage
                inquiry={inquiries.find((inquiry) => inquiry.id === selectedInquiry.id) || selectedInquiry}
                leads={leads}
                categories={productCategories}
                users={users}
                comments={comments}
                existingQuote={quotes.find((quote) => quote.inquiryId === selectedInquiry.id) || null}
                relatedQuotes={quotes.filter((quote) => quote.inquiryId === selectedInquiry.id)}
                offers={offers}
                suppliers={visibleSuppliers}
                onBack={returnFromInquiryDetail}
                onUpdateInquiry={handleInquiryStatusChange}
                onCreateQuote={handleCreateQuoteFromInquiry}
                onOpenQuote={(quote) => openQuoteDetail(quote, 'inquiryDetail')}
                onOpenOffer={(offer) => openOrderDetails(offer, 'inquiryDetail')}
                openLeadProfile={openLeadProfile}
                onAddComment={({ entityId, entityName }) => openCommentsModal(entityId, entityName)}
                onAddItem={(itemData) => handleAddInquiryItem(selectedInquiry.id, itemData)}
                onUpdateItem={(itemId, patch) => handleUpdateInquiryItem(selectedInquiry.id, itemId, patch)}
                onDeleteItem={(itemId) => handleDeleteInquiryItem(selectedInquiry.id, itemId)}
                onOpenCategoryDetail={(item) => openCategoryDetail(
                  inquiries.find((inquiry) => inquiry.id === selectedInquiry.id) || selectedInquiry,
                  item,
                )}
                onCreateQuoteForItem={(inquiryId, itemId) => handleCreateQuoteForInquiryItem(inquiryId, itemId)}
                onCreateOfferFromQuotes={requestCreateOfferFromQuotes}
                onCopyInquiry={handleCopyInquiry}
                t={t}
              />
            )}
            {currentView === 'categoryDetail' && selectedInquiry && selectedInquiryItem && (
              <CategoryDetailPage
                item={(inquiries.find((inquiry) => inquiry.id === selectedInquiry.id) || selectedInquiry).items
                  .find((item) => item.id === selectedInquiryItem.id) || selectedInquiryItem}
                suppliers={visibleSuppliers}
                supplierProducts={visibleSupplierProducts}
                products={visibleProducts}
                services={serviceItems}
                onBack={returnFromCategoryDetail}
                draft={wizardDrafts[selectedInquiryItem.id] || null}
                onDraftChange={(itemId, nextDraft) => setWizardDrafts((prev) => ({ ...prev, [itemId]: nextDraft }))}
                onSaveWizardResult={(itemId, wizardResult) => {
                  handleUpdateInquiryItem(selectedInquiry.id, itemId, { wizardResult, sourcingStatus: 'Sourced' });
                  setWizardDrafts((prev) => {
                    if (!(itemId in prev)) return prev;
                    const next = { ...prev };
                    delete next[itemId];
                    return next;
                  });
                  returnFromCategoryDetail();
                }}
                onOpenProduct={(product) => openProductDetails(product, 'categoryDetail')}
                onOpenSupplier={(supplier) => openSupplierProfile(supplier, 'categoryDetail')}
                onAddProduct={(onReady) => openWizardProductChooser(selectedInquiryItem.categoryId, onReady)}
                onCopyProduct={(onReady) => openWizardCopyProduct(selectedInquiryItem.categoryId, onReady)}
                t={t}
              />
            )}
            {currentView === 'quoteDetail' && selectedQuote && (
              <QuoteDetailPage
                quote={quotes.find((quote) => quote.id === selectedQuote.id) || selectedQuote}
                leads={leads}
                products={visibleProducts}
                categories={productCategories}
                services={serviceItems}
                logisticsItems={logisticsItems}
                suppliers={visibleSuppliers}
                supplierProducts={visibleSupplierProducts}
                certificates={certificates}
                comments={comments}
                currentUser={currentUser}
                onBack={returnFromQuoteDetail}
                onUpdateQuote={handleUpdateQuote}
                onSaveQuotation={handleSaveQuoteQuotation}
                onConvertToOffer={handleConvertQuoteToOffer}
                onOpenConvertedOffer={(offerId) => {
                  const offer = offers.find((item) => item.id === offerId);
                  if (offer) openOrderDetails(offer, 'offers');
                }}
                onOpenSourceInquiry={(inquiryId) => {
                  const inquiry = inquiries.find((item) => item.id === inquiryId);
                  if (inquiry) openInquiryDetail(inquiry, 'quoteDetail');
                }}
                onAddComment={({ entityId, entityName }) => openCommentsModal(entityId, entityName)}
                onSaveNewProduct={handleSaveNewInquiryProduct}
                onSaveSupplierQuote={handleSaveSupplierQuote}
                onSetQuoteStatus={handleSetQuoteStatus}
                onConnectToOffer={requestConnectQuotesToOffer}
                onCreateOfferFromQuote={(quoteId) => requestCreateOfferFromQuotes([quoteId])}
                offers={offers.filter((offer) => offer.type === 'Offer' && !offer.archived)}
                canDelete={currentUserCanDelete}
                t={t}
              />
            )}
            {currentView === 'products' && (
              <ProductsManagementPage
                products={visibleProducts}
                setProducts={setProducts}
                categories={productCategories}
                setCategories={setProductCategories}
                services={serviceItems}
                setServices={setServiceItems}
                logisticsItems={logisticsItems}
                setLogisticsItems={setLogisticsItems}
                suppliers={visibleSuppliers}
                supplierProducts={visibleSupplierProducts}
                productStatusFilter={productStatusFilter}
                setProductStatusFilter={setProductStatusFilter}
                productNameFilter={productNameFilter}
                setProductNameFilter={setProductNameFilter}
                productCategoryFilter={productCategoryFilter}
                setProductCategoryFilter={setProductCategoryFilter}
                productTypeFilter={productTypeFilter}
                setProductTypeFilter={setProductTypeFilter}
                setEditingProduct={setEditingProduct}
                openProductDetails={openProductDetails}
                showModal={showModal}
                onOpenCreateChooser={handleOpenProductCreateChooser}
                onCopyProduct={handleOpenCopyProduct}
                canManage={canManageProducts(currentUser)}
              />
            )}
            {currentView === 'productDetails' && selectedProduct && (
              <ProductDetailsPage
                product={visibleProducts.find((product) => product.id === selectedProduct.id) || selectedProduct}
                category={productCategories.find((category) => category.id === selectedProduct.categoryId)}
                suppliers={visibleSuppliers}
                supplierProducts={visibleSupplierProducts}
                documents={productDocuments}
                products={visibleProducts}
                certificates={certificates}
                productionNotes={productionNotes}
                canManage={canManageProducts(currentUser)}
                t={t}
                onBack={() => setCurrentView(productDetailsReturnView)}
                onEdit={(product) => {
                  setEditingProduct(product);
                  showModal('editProduct');
                }}
                onCopy={handleOpenCopyProduct}
                onAddDocument={handleAddProductDocument}
                onEditDocument={handleEditProductDocument}
                onDeleteDocument={handleDeleteProductDocument}
                onOpenProduct={openProductDetails}
                onVerifyCertificate={handleVerifyCertificate}
                onAddProductionNote={handleAddProductionNote}
              />
            )}
            {currentView === 'personalProfile' && (
              <PersonalProfilePage
                currentUser={currentUser}
                onSave={handlePersonalProfileSave}
                onCancel={() => setCurrentView('followUps')}
              />
            )}
            {currentView === 'users' && (
              <UsersManagementPage
                users={users}
                currentUser={currentUser}
                setUsers={setUsers}
                handleUserStatusChange={handleUserStatusChange}
                showModal={showModal}
                userForm={userForm}
                canDelete={currentUserCanDelete}
              />
            )}
          </Content>

          {renderModal()}
          <ProductDocumentModal
            open={isProductDocumentModalOpen}
            onCancel={() => {
              setIsProductDocumentModalOpen(false);
              setEditingProductDocument(null);
            }}
            onSubmit={handleProductDocumentSubmit}
            editingDocument={editingProductDocument}
            product={selectedProduct}
            currentUser={currentUser}
          />
          <Modal
            title={t('Select Supplier')}
            open={Boolean(supplierPicker)}
            onCancel={() => setSupplierPicker(null)}
            onOk={confirmSupplierPicker}
            okText={t('Confirm')}
            cancelText={t('Cancel')}
          >
            <Space direction="vertical" size={20} style={{ width: '100%' }}>
              {supplierPicker?.quoteIds.map((quoteId) => {
                const options = supplierPicker.optionsByQuoteId[quoteId] || [];
                if (options.length <= 1) return null;
                const quote = quotes.find((item) => item.id === quoteId);
                return (
                  <div key={quoteId}>
                    <Typography.Text strong>
                      {quote?.productName} ({quoteId})
                    </Typography.Text>
                    <Radio.Group
                      value={supplierPicker.selections[quoteId]}
                      onChange={(event) => setSupplierPicker((prev) => ({
                        ...prev,
                        selections: { ...prev.selections, [quoteId]: event.target.value },
                      }))}
                      style={{ display: 'block', marginTop: 8 }}
                    >
                      <Space direction="vertical">
                        {options.map((option) => (
                          <Radio key={option.supplierId} value={option.supplierId}>
                            {option.supplierName} — {Number(option.data.unitPrice || 0).toLocaleString('da-DK')} DKK/{t('unit')}
                          </Radio>
                        ))}
                      </Space>
                    </Radio.Group>
                  </div>
                );
              })}
            </Space>
          </Modal>
        </Layout>
      </Layout>
      </ConfigProvider>
    </I18nProvider>
  );
}
